// FILE LOCATION: src/utils/guidelineResolver.ts
// Guideline Resolver — maps LLM evidence output to verified, document-level citations.
// Data flow: LLM output → parse → resolve → validate → render

// ============================================
// TYPES
// ============================================

/** Structured evidence item from LLM output */
export interface EvidenceItem {
  org: string;
  document_title: string;
  document_type: string;
  year: number | null;
  recommendation_text: string;
  url: string | null;
  tier: "TIER_1" | "TIER_2";
}

/** Resolved evidence item after verification */
export interface ResolvedEvidence extends EvidenceItem {
  verified: boolean;
  canonical_url: string | null;
  url_quality: UrlQuality;
  display_label: string; // e.g. "ACOG Practice Bulletin No. 128 (2012)"
}

export type UrlQuality =
  | "DOCUMENT_SPECIFIC"
  | "GENERIC_LANDING"
  | "INVALID"
  | "UNKNOWN";

// ============================================
// CURATED GUIDELINE INDEX
// Seeded from GUIDELINE_DB entries with document-level specificity.
// Expand as new specialties are added.
// ============================================

interface CuratedGuideline {
  org: string;
  document_title: string;
  document_type: string;
  year: number | null;
  canonical_url: string;
  tags: string[]; // topic keywords for fuzzy matching
}

const CURATED_INDEX: CuratedGuideline[] = [
  // --- ACR APPROPRIATENESS CRITERIA ---
  {
    org: "ACR",
    document_title: "ACR Appropriateness Criteria: Low Back Pain",
    document_type: "Appropriateness Criteria",
    year: 2021,
    canonical_url: "https://acsearch.acr.org/docs/69483/Narrative/",
    tags: [
      "low back pain",
      "lumbar",
      "radiculopathy",
      "sciatica",
      "mri lumbar",
    ],
  },
  {
    org: "ACR",
    document_title:
      "ACR Appropriateness Criteria: Neck Pain or Cervical Radiculopathy",
    document_type: "Appropriateness Criteria",
    year: 2022,
    canonical_url: "https://acsearch.acr.org/docs/69426/Narrative/",
    tags: ["neck pain", "cervical", "cervical radiculopathy", "mri cervical"],
  },
  {
    org: "ACR",
    document_title: "ACR Appropriateness Criteria: Headache",
    document_type: "Appropriateness Criteria",
    year: 2022,
    canonical_url: "https://acsearch.acr.org/docs/69482/Narrative/",
    tags: ["headache", "migraine", "mri brain", "ct head"],
  },
  {
    org: "ACR",
    document_title: "ACR Appropriateness Criteria: Shoulder Pain — Atraumatic",
    document_type: "Appropriateness Criteria",
    year: 2022,
    canonical_url: "https://acsearch.acr.org/docs/69433/Narrative/",
    tags: ["shoulder pain", "rotator cuff", "mri shoulder"],
  },
  {
    org: "ACR",
    document_title: "ACR Appropriateness Criteria: Chronic Knee Pain",
    document_type: "Appropriateness Criteria",
    year: 2022,
    canonical_url: "https://acsearch.acr.org/docs/69494/Narrative/",
    tags: ["knee pain", "meniscus", "acl", "mri knee"],
  },
  {
    org: "ACR",
    document_title: "ACR Appropriateness Criteria: Chronic Hip Pain",
    document_type: "Appropriateness Criteria",
    year: 2023,
    canonical_url: "https://acsearch.acr.org/docs/69420/Narrative/",
    tags: ["hip pain", "avascular necrosis", "labral tear", "mri hip"],
  },
  {
    org: "ACR",
    document_title: "ACR Appropriateness Criteria: Abnormal Uterine Bleeding",
    document_type: "Appropriateness Criteria",
    year: 2020,
    canonical_url: "https://acsearch.acr.org/docs/69458/Narrative/",
    tags: [
      "abnormal uterine bleeding",
      "aub",
      "menorrhagia",
      "tvus",
      "transvaginal",
      "pelvic ultrasound",
      "vaginal bleeding",
    ],
  },
  {
    org: "ACR",
    document_title:
      "ACR Appropriateness Criteria: Acute Pelvic Pain — Reproductive Age",
    document_type: "Appropriateness Criteria",
    year: 2022,
    canonical_url: "https://acsearch.acr.org/docs/69503/Narrative/",
    tags: ["pelvic pain", "ovarian cyst", "ectopic pregnancy", "tvus"],
  },
  {
    org: "ACR",
    document_title:
      "ACR Appropriateness Criteria: Chest Pain — High Probability of CAD",
    document_type: "Appropriateness Criteria",
    year: 2022,
    canonical_url: "https://acsearch.acr.org/docs/69337/Narrative/",
    tags: [
      "chest pain",
      "cad",
      "coronary artery disease",
      "stress test",
      "ccta",
    ],
  },
  {
    org: "ACR",
    document_title:
      "ACR Appropriateness Criteria: Suspected Pulmonary Embolism",
    document_type: "Appropriateness Criteria",
    year: 2022,
    canonical_url: "https://acsearch.acr.org/docs/69404/Narrative/",
    tags: ["pulmonary embolism", "pe", "cta chest", "dvt"],
  },
  {
    org: "ACR",
    document_title: "ACR Appropriateness Criteria: Cerebrovascular Disease",
    document_type: "Appropriateness Criteria",
    year: 2021,
    canonical_url: "https://acsearch.acr.org/docs/69478/Narrative/",
    tags: ["stroke", "tia", "cerebrovascular", "cta head", "carotid"],
  },
  {
    org: "ACR",
    document_title: "ACR Appropriateness Criteria: Myelopathy",
    document_type: "Appropriateness Criteria",
    year: 2022,
    canonical_url: "https://acsearch.acr.org/docs/3111292/Narrative/",
    tags: ["myelopathy", "spinal cord compression"],
  },
  {
    org: "ACR",
    document_title: "ACR Appropriateness Criteria: Hematuria",
    document_type: "Appropriateness Criteria",
    year: 2022,
    canonical_url: "https://acsearch.acr.org/docs/69490/Narrative/",
    tags: ["hematuria", "blood in urine", "ct urogram"],
  },
  {
    org: "ACR",
    document_title:
      "ACR Appropriateness Criteria: Acute Onset Flank Pain — Suspected Stone",
    document_type: "Appropriateness Criteria",
    year: 2022,
    canonical_url: "https://acsearch.acr.org/docs/69362/Narrative/",
    tags: ["kidney stone", "renal colic", "flank pain", "nephrolithiasis"],
  },
  {
    org: "ACR",
    document_title:
      "ACR Appropriateness Criteria: Dyspnea — Suspected Cardiac Origin",
    document_type: "Appropriateness Criteria",
    year: 2021,
    canonical_url: "https://acsearch.acr.org/docs/69407/Narrative/",
    tags: ["dyspnea", "shortness of breath", "heart failure", "echocardiogram"],
  },
  {
    org: "ACR",
    document_title: "ACR Appropriateness Criteria: Right Lower Quadrant Pain",
    document_type: "Appropriateness Criteria",
    year: 2022,
    canonical_url: "https://acsearch.acr.org/docs/69357/Narrative/",
    tags: ["right lower quadrant", "appendicitis", "ct abdomen"],
  },
  {
    org: "ACR",
    document_title: "ACR Appropriateness Criteria: Right Upper Quadrant Pain",
    document_type: "Appropriateness Criteria",
    year: 2022,
    canonical_url: "https://acsearch.acr.org/docs/69474/Narrative/",
    tags: ["right upper quadrant", "gallbladder", "cholecystitis"],
  },
  {
    org: "ACR",
    document_title: "ACR Appropriateness Criteria: Seizures and Epilepsy",
    document_type: "Appropriateness Criteria",
    year: 2022,
    canonical_url: "https://acsearch.acr.org/docs/69479/Narrative/",
    tags: ["seizure", "epilepsy", "mri brain"],
  },
  {
    org: "ACR",
    document_title: "ACR Appropriateness Criteria: Dementia",
    document_type: "Appropriateness Criteria",
    year: 2022,
    canonical_url: "https://acsearch.acr.org/docs/3111509/Narrative/",
    tags: ["dementia", "alzheimer", "cognitive decline", "memory loss"],
  },
  {
    org: "ACR",
    document_title:
      "ACR Appropriateness Criteria: Nonischemic Myocardial Disease",
    document_type: "Appropriateness Criteria",
    year: 2023,
    canonical_url: "https://acsearch.acr.org/docs/3188470/Narrative/",
    tags: ["cardiomyopathy", "heart failure", "cardiac mri", "myocarditis"],
  },
  {
    org: "ACR",
    document_title: "ACR Appropriateness Criteria: Lung Cancer Screening",
    document_type: "Appropriateness Criteria",
    year: 2023,
    canonical_url: "https://acsearch.acr.org/docs/3209082/Narrative/",
    tags: ["lung cancer screening", "ldct", "lung nodule"],
  },

  // --- ACOG ---
  {
    org: "ACOG",
    document_title:
      "ACOG Practice Bulletin No. 128: Diagnosis of Abnormal Uterine Bleeding in Reproductive-Aged Women",
    document_type: "Practice Bulletin",
    year: 2012,
    canonical_url:
      "https://www.acog.org/clinical/clinical-guidance/practice-bulletin/articles/2012/07/diagnosis-of-abnormal-uterine-bleeding-in-reproductive-aged-women",
    tags: [
      "abnormal uterine bleeding",
      "aub",
      "tvus",
      "transvaginal",
      "endometrial biopsy",
      "vaginal bleeding",
      "pelvic ultrasound",
    ],
  },
  {
    org: "ACOG",
    document_title:
      "ACOG Practice Bulletin No. 136: Management of Abnormal Uterine Bleeding Associated with Ovulatory Dysfunction",
    document_type: "Practice Bulletin",
    year: 2013,
    canonical_url:
      "https://www.acog.org/clinical/clinical-guidance/practice-bulletin/articles/2013/07/management-of-abnormal-uterine-bleeding-associated-with-ovulatory-dysfunction",
    tags: [
      "anovulatory bleeding",
      "ovulatory dysfunction",
      "aub-o",
      "hormonal therapy",
    ],
  },
  {
    org: "ACOG",
    document_title:
      "ACOG Practice Bulletin No. 228: Management of Symptomatic Uterine Leiomyomas",
    document_type: "Practice Bulletin",
    year: 2021,
    canonical_url:
      "https://www.acog.org/clinical/clinical-guidance/practice-bulletin/articles/2021/06/management-of-symptomatic-uterine-leiomyomas",
    tags: [
      "fibroids",
      "leiomyoma",
      "myomectomy",
      "uterine artery embolization",
      "heavy menstrual bleeding",
    ],
  },
  // ACOG PB 149 (Endometrial Cancer) removed — bulletin officially withdrawn by ACOG
  {
    org: "ACOG",
    document_title:
      "ACOG Committee Opinion: Choosing the Route of Hysterectomy for Benign Disease",
    document_type: "Committee Opinion",
    year: 2017,
    canonical_url:
      "https://www.acog.org/clinical/clinical-guidance/committee-opinion/articles/2017/06/choosing-the-route-of-hysterectomy-for-benign-disease",
    tags: ["hysterectomy", "laparoscopic hysterectomy", "vaginal hysterectomy"],
  },
  {
    org: "ACOG",
    document_title:
      "ACOG Practice Bulletin No. 114: Management of Endometriosis",
    document_type: "Practice Bulletin",
    year: 2010,
    canonical_url:
      "https://www.acog.org/clinical/clinical-guidance/practice-bulletin/articles/2010/07/management-of-endometriosis",
    tags: [
      "endometriosis",
      "pelvic pain",
      "dysmenorrhea",
      "laparoscopy endometriosis",
      "gonadotropin releasing hormone agonist",
      "gnrh agonist",
      "elagolix",
      "orilissa",
      "deep infiltrating endometriosis",
      "endometrioma",
    ],
  },
  {
    org: "ACOG",
    document_title: "ACOG Practice Bulletin No. 194: Polycystic Ovary Syndrome",
    document_type: "Practice Bulletin",
    year: 2018,
    canonical_url:
      "https://www.acog.org/clinical/clinical-guidance/practice-bulletin/articles/2018/06/polycystic-ovary-syndrome",
    tags: [
      "polycystic ovary syndrome",
      "pcos",
      "hyperandrogenism",
      "anovulation",
      "metformin pcos",
      "clomiphene",
      "letrozole",
      "insulin resistance",
    ],
  },
  {
    org: "ACOG",
    document_title: "ACOG Practice Bulletin No. 193: Tubal Ectopic Pregnancy",
    document_type: "Practice Bulletin",
    year: 2018,
    canonical_url:
      "https://www.acog.org/clinical/clinical-guidance/practice-bulletin/articles/2018/03/tubal-ectopic-pregnancy",
    tags: [
      "ectopic pregnancy",
      "tubal ectopic",
      "methotrexate ectopic",
      "salpingectomy",
      "salpingostomy",
      "hcg monitoring",
    ],
  },
  {
    org: "ACOG/AUGS",
    document_title: "ACOG Practice Bulletin No. 214: Pelvic Organ Prolapse",
    document_type: "Practice Bulletin",
    year: 2019,
    canonical_url: "https://pubmed.ncbi.nlm.nih.gov/31651832/",
    tags: [
      "pelvic organ prolapse",
      "pop",
      "cystocele",
      "rectocele",
      "uterine prolapse",
      "vaginal vault prolapse",
      "sacrocolpopexy",
      "pessary",
      "colporrhaphy",
      "pelvic floor",
      "uterosacral ligament suspension",
      "sacrospinous fixation",
      "pop-q staging",
      "vaginal bulge",
      "pelvic reconstructive surgery",
    ],
  },
  {
    org: "ACOG/AUGS",
    document_title:
      "ACOG Practice Bulletin No. 155: Urinary Incontinence in Women",
    document_type: "Practice Bulletin",
    year: 2015,
    canonical_url:
      "https://www.acog.org/clinical/clinical-guidance/practice-bulletin/articles/2015/11/urinary-incontinence-in-women",
    tags: [
      "urinary incontinence",
      "stress urinary incontinence",
      "sui",
      "urgency incontinence",
      "overactive bladder",
      "oab",
      "midurethral sling",
      "pelvic floor physical therapy",
      "urodynamics",
      "bladder training",
      "anticholinergic",
      "mirabegron",
      "sacral neuromodulation",
      "ptns",
      "mixed incontinence",
    ],
  },
  {
    org: "ACOG",
    document_title:
      "ACOG Practice Bulletin No. 226: Screening for Fetal Chromosomal Abnormalities",
    document_type: "Practice Bulletin",
    year: 2020,
    canonical_url: "https://pubmed.ncbi.nlm.nih.gov/32804883/",
    tags: [
      "nipt",
      "cell free dna",
      "noninvasive prenatal testing",
      "prenatal screening",
      "aneuploidy screening",
      "trisomy 21",
      "down syndrome",
      "nuchal translucency",
      "first trimester screening",
      "quad screen",
      "amniocentesis",
      "cvs",
      "chorionic villus sampling",
      "fetal chromosomal abnormalities",
      "cfDNA",
      "genetic screening pregnancy",
    ],
  },
  {
    org: "ACOG",
    document_title:
      "ACOG Practice Bulletin No. 190: Gestational Diabetes Mellitus",
    document_type: "Practice Bulletin",
    year: 2018,
    canonical_url: "https://pubmed.ncbi.nlm.nih.gov/29370047/",
    tags: [
      "gestational diabetes",
      "gdm",
      "glucose tolerance test",
      "ogtt",
      "insulin pregnancy",
      "metformin pregnancy",
      "glyburide pregnancy",
      "macrosomia",
      "glucose screening",
      "gestational diabetes screening",
      "a1c pregnancy",
      "fetal surveillance gdm",
      "postpartum glucose screening",
    ],
  },
  {
    org: "ACOG",
    document_title:
      "ACOG Practice Bulletin No. 222: Gestational Hypertension and Preeclampsia",
    document_type: "Practice Bulletin",
    year: 2020,
    canonical_url: "https://pubmed.ncbi.nlm.nih.gov/32443079/",
    tags: [
      "preeclampsia",
      "gestational hypertension",
      "hellp syndrome",
      "eclampsia",
      "magnesium sulfate",
      "severe features preeclampsia",
      "low dose aspirin pregnancy",
      "aspirin preeclampsia prevention",
      "blood pressure pregnancy",
      "proteinuria pregnancy",
      "delivery timing preeclampsia",
      "antihypertensive pregnancy",
      "sflt1 plgf ratio",
    ],
  },
  {
    org: "ASCCP",
    document_title:
      "2019 ASCCP Risk-Based Management Consensus Guidelines for Abnormal Cervical Cancer Screening Tests and Cancer Precursors",
    document_type: "Consensus Guideline",
    year: 2020,
    canonical_url: "https://pubmed.ncbi.nlm.nih.gov/32243307/",
    tags: [
      "cervical dysplasia",
      "cervical cancer screening",
      "colposcopy",
      "leep",
      "cin",
      "cervical intraepithelial neoplasia",
      "hpv",
      "abnormal pap",
      "ascus",
      "lsil",
      "hsil",
      "hpv positive",
      "cervical biopsy",
      "loop electrosurgical excision",
      "cervical conization",
      "cin2",
      "cin3",
      "cold knife cone",
    ],
  },
  {
    org: "ASRM",
    document_title:
      "ASRM Evidence-Based Treatments for Couples with Unexplained Infertility: A Guideline",
    document_type: "Clinical Practice Guideline",
    year: 2020,
    canonical_url: "https://pubmed.ncbi.nlm.nih.gov/32106976/",
    tags: [
      "infertility",
      "unexplained infertility",
      "iui",
      "intrauterine insemination",
      "ivf",
      "in vitro fertilization",
      "ovarian stimulation",
      "clomiphene",
      "letrozole",
      "gonadotropins",
      "fertility treatment",
      "assisted reproductive technology",
      "art",
      "icsi",
      "ovulation induction",
      "fertility workup",
    ],
  },
  {
    org: "ACOG",
    document_title:
      "ACOG Practice Bulletin No. 205: Vaginal Birth After Cesarean Delivery",
    document_type: "Practice Bulletin",
    year: 2019,
    canonical_url: "https://pubmed.ncbi.nlm.nih.gov/30681543/",
    tags: [
      "vbac",
      "vaginal birth after cesarean",
      "tolac",
      "trial of labor after cesarean",
      "repeat cesarean",
      "uterine rupture",
      "prior cesarean",
      "low transverse incision",
      "vbac calculator",
      "failed tolac",
      "cesarean delivery",
      "elective repeat cesarean",
      "vba2c",
    ],
  },
  {
    org: "ACOG",
    document_title:
      "ACOG Practice Bulletin No. 234: Prediction and Prevention of Spontaneous Preterm Birth",
    document_type: "Practice Bulletin",
    year: 2021,
    canonical_url: "https://pubmed.ncbi.nlm.nih.gov/34293771/",
    tags: [
      "preterm birth",
      "preterm labor",
      "cerclage",
      "cervical insufficiency",
      "short cervix",
      "cervical length",
      "progesterone preterm birth",
      "vaginal progesterone",
      "17-ohpc",
      "makena",
      "pprom",
      "preterm premature rupture of membranes",
      "tocolysis",
      "fetal fibronectin",
      "cervical length screening",
      "history indicated cerclage",
      "rescue cerclage",
    ],
  },

  // --- ACC/AHA ---
  {
    org: "ACC/AHA",
    document_title:
      "ACC/AHA Guideline for Evaluation and Diagnosis of Chest Pain",
    document_type: "Clinical Practice Guideline",
    year: 2021,
    canonical_url:
      "https://www.ahajournals.org/doi/10.1161/CIR.0000000000001029",
    tags: [
      "chest pain",
      "angina",
      "acute coronary syndrome",
      "stress test",
      "troponin",
    ],
  },
  {
    org: "ACC/AHA",
    document_title:
      "ACC/AHA/HFSA Guideline for the Management of Heart Failure",
    document_type: "Clinical Practice Guideline",
    year: 2022,
    canonical_url:
      "https://www.ahajournals.org/doi/10.1161/CIR.0000000000001063",
    tags: [
      "heart failure",
      "chf",
      "hfref",
      "hfpef",
      "ejection fraction",
      "entresto",
      "echocardiogram",
    ],
  },
  {
    org: "ACC/AHA",
    document_title:
      "ACC/AHA Guideline for the Management of Valvular Heart Disease",
    document_type: "Clinical Practice Guideline",
    year: 2020,
    canonical_url:
      "https://www.ahajournals.org/doi/10.1161/CIR.0000000000001020",
    tags: [
      "aortic stenosis",
      "mitral regurgitation",
      "tavr",
      "valve replacement",
      "echocardiogram",
    ],
  },
  {
    org: "ACC/AHA",
    document_title:
      "ACC/AHA/HRS Guideline for the Management of Atrial Fibrillation",
    document_type: "Clinical Practice Guideline",
    year: 2023,
    canonical_url:
      "https://www.ahajournals.org/doi/10.1161/CIR.0000000000001193",
    tags: [
      "atrial fibrillation",
      "afib",
      "anticoagulation",
      "ablation",
      "eliquis",
      "xarelto",
    ],
  },
  {
    org: "ACC/AHA/SCAI",
    document_title:
      "ACC/AHA/SCAI Guideline for Coronary Artery Revascularization",
    document_type: "Clinical Practice Guideline",
    year: 2021,
    canonical_url: "https://pubmed.ncbi.nlm.nih.gov/34895950/",
    tags: [
      "coronary revascularization",
      "pci",
      "percutaneous coronary intervention",
      "cabg",
      "coronary artery bypass",
      "stent",
      "left main disease",
      "multivessel disease",
      "syntax score",
      "heart team",
      "stemi",
      "nstemi",
      "acute coronary syndrome",
      "drug eluting stent",
      "radial access",
    ],
  },
  {
    org: "ACC/AHA",
    document_title:
      "ACC/AHA Guideline for the Management of Lower Extremity Peripheral Artery Disease",
    document_type: "Clinical Practice Guideline",
    year: 2024,
    canonical_url: "https://pubmed.ncbi.nlm.nih.gov/38743805/",
    tags: [
      "peripheral artery disease",
      "pad",
      "claudication",
      "critical limb ischemia",
      "chronic limb threatening ischemia",
      "ankle brachial index",
      "abi",
      "peripheral vascular disease",
      "endovascular",
      "bypass graft",
      "amputation prevention",
      "acute limb ischemia",
      "supervised exercise therapy",
    ],
  },
  {
    org: "ACC/AHA",
    document_title:
      "ACC/AHA Guideline for the Prevention, Detection, Evaluation, and Management of High Blood Pressure in Adults",
    document_type: "Clinical Practice Guideline",
    year: 2017,
    canonical_url: "https://pubmed.ncbi.nlm.nih.gov/29133356/",
    tags: [
      "hypertension",
      "blood pressure",
      "antihypertensive",
      "resistant hypertension",
      "secondary hypertension",
      "sprint trial",
      "blood pressure target",
      "ambulatory blood pressure monitoring",
    ],
  },
  {
    org: "AHA/ACC",
    document_title: "AHA/ACC Guideline on the Management of Blood Cholesterol",
    document_type: "Clinical Practice Guideline",
    year: 2018,
    canonical_url: "https://pubmed.ncbi.nlm.nih.gov/30586774/",
    tags: [
      "cholesterol",
      "statin",
      "pcsk9 inhibitor",
      "ezetimibe",
      "ldl cholesterol",
      "hyperlipidemia",
      "familial hypercholesterolemia",
      "ascvd risk",
      "repatha",
      "praluent",
      "high intensity statin",
      "lipid management",
    ],
  },

  // --- NASS ---
  {
    org: "NASS",
    document_title:
      "NASS Clinical Guideline: Diagnosis and Treatment of Lumbar Disc Herniation with Radiculopathy",
    document_type: "Clinical Practice Guideline",
    year: 2014,
    canonical_url: "https://pubmed.ncbi.nlm.nih.gov/24239490/",
    tags: [
      "lumbar disc herniation",
      "radiculopathy",
      "discectomy",
      "microdiscectomy",
      "epidural steroid injection",
      "sciatica",
      "herniated disc",
      "nerve root compression",
      "cauda equina",
      "disc extrusion",
      "disc protrusion",
      "leg pain radiculopathy",
      "lumbar disc surgery",
    ],
  },
  {
    org: "NASS",
    document_title:
      "NASS Clinical Guideline: Diagnosis and Treatment of Cervical Radiculopathy from Degenerative Disorders",
    document_type: "Clinical Practice Guideline",
    year: 2011,
    canonical_url: "https://pubmed.ncbi.nlm.nih.gov/21168100/",
    tags: [
      "cervical radiculopathy",
      "acdf",
      "cervical disc herniation",
      "anterior cervical discectomy fusion",
      "cervical foraminal stenosis",
      "cervical disc replacement",
      "cervical laminoforaminotomy",
      "arm pain radiculopathy",
      "cervical spine surgery",
    ],
  },
  {
    org: "NASS",
    document_title:
      "NASS Clinical Guideline: Diagnosis and Treatment of Degenerative Lumbar Spinal Stenosis (Update)",
    document_type: "Clinical Practice Guideline",
    year: 2013,
    canonical_url: "https://pubmed.ncbi.nlm.nih.gov/23830297/",
    tags: [
      "lumbar stenosis",
      "spinal stenosis",
      "neurogenic claudication",
      "laminectomy",
      "lumbar decompression",
      "lumbar fusion stenosis",
      "epidural injection stenosis",
      "walking tolerance",
      "central stenosis",
      "lateral recess stenosis",
      "foraminal stenosis",
    ],
  },
  {
    org: "NASS",
    document_title: "NASS Coverage Policy: Lumbar Fusion",
    document_type: "Coverage Policy",
    year: 2014,
    canonical_url: "https://www.spine.org/Coverage",
    tags: [
      "lumbar fusion",
      "spinal fusion",
      "spondylolisthesis",
      "degenerative disc disease",
    ],
  },
  {
    org: "NASS",
    document_title:
      "NASS Coverage Recommendation: Cervical Artificial Disc Replacement",
    document_type: "Coverage Policy",
    year: 2022,
    canonical_url: "https://www.spine.org/Coverage",
    tags: [
      "cervical artificial disc",
      "cervical disc replacement",
      "cervical arthroplasty",
      "total disc arthroplasty",
      "cervical tda",
      "acdf alternative",
      "adjacent segment disease",
      "motion preservation",
    ],
  },
  {
    org: "ISASS",
    document_title:
      "ISASS Policy 2020 Update: Minimally Invasive Surgical Sacroiliac Joint Fusion — Coverage Indications, Limitations, and Medical Necessity",
    document_type: "Coverage Policy",
    year: 2020,
    canonical_url: "https://pubmed.ncbi.nlm.nih.gov/33560247/",
    tags: [
      "sacroiliac joint fusion",
      "si joint",
      "sij fusion",
      "sacroiliac dysfunction",
      "sacroiliitis",
      "minimally invasive si fusion",
      "pelvic pain",
      "failed back surgery",
      "si joint block",
    ],
  },
  {
    org: "ACP",
    document_title:
      "ACP Clinical Practice Guideline: Noninvasive Treatments for Acute, Subacute, and Chronic Low Back Pain",
    document_type: "Clinical Practice Guideline",
    year: 2017,
    canonical_url: "https://pubmed.ncbi.nlm.nih.gov/28192789/",
    tags: [
      "low back pain",
      "chronic low back pain",
      "acute back pain",
      "nsaids",
      "physical therapy",
      "nonpharmacologic treatment",
      "spinal manipulation",
      "exercise therapy",
      "conservative management",
    ],
  },
  {
    org: "NASS",
    document_title:
      "NASS Evidence-Based Clinical Guideline: Diagnosis and Treatment of Low Back Pain",
    document_type: "Clinical Practice Guideline",
    year: 2020,
    canonical_url: "https://pubmed.ncbi.nlm.nih.gov/32333996/",
    tags: [
      "low back pain",
      "nonspecific low back pain",
      "lumbago",
      "lumbar pain",
      "conservative treatment back pain",
      "physical therapy back pain",
      "nsaid back pain",
      "imaging back pain",
      "red flags back pain",
      "chronic low back pain",
      "acute low back pain",
      "muscle relaxant",
      "back pain diagnosis",
    ],
  },
  {
    org: "AOSpine",
    document_title:
      "AOSpine Thoracolumbar Spine Injury Classification System: Surgical Algorithm",
    document_type: "Clinical Practice Guideline",
    year: 2016,
    canonical_url: "https://pubmed.ncbi.nlm.nih.gov/25953527/",
    tags: [
      "thoracolumbar trauma",
      "burst fracture",
      "spinal fracture",
      "thoracolumbar injury",
      "aospine injury score",
      "tl aosis",
      "fracture fixation",
      "spinal cord injury",
      "compression fracture",
      "chance fracture",
    ],
  },
  {
    org: "ISASS",
    document_title:
      "ISASS Policy Statement: Lumbar Artificial Disc — Coverage Indications and Medical Necessity",
    document_type: "Coverage Policy",
    year: 2015,
    canonical_url: "https://pubmed.ncbi.nlm.nih.gov/25785243/",
    tags: [
      "lumbar artificial disc",
      "lumbar disc replacement",
      "lumbar arthroplasty",
      "lumbar total disc replacement",
      "lumbar tdr",
      "degenerative disc disease",
      "motion preservation",
      "adjacent segment disease",
    ],
  },
  {
    org: "ISASS",
    document_title:
      "ISASS Policy 2018: Vertebral Augmentation — Coverage Indications, Limitations, and Medical Necessity",
    document_type: "Coverage Policy",
    year: 2018,
    canonical_url: "https://pubmed.ncbi.nlm.nih.gov/30805279/",
    tags: [
      "vertebral augmentation",
      "vertebroplasty",
      "kyphoplasty",
      "balloon kyphoplasty",
      "vertebral compression fracture",
      "cement augmentation",
      "osteoporotic fracture",
      "pmma",
    ],
  },
  {
    org: "NASS",
    document_title:
      "NASS Coverage Recommendation: Lumbar Artificial Disc Replacement",
    document_type: "Coverage Policy",
    year: 2022,
    canonical_url: "https://www.spine.org/Coverage",
    tags: [
      "lumbar artificial disc",
      "lumbar disc replacement",
      "lumbar arthroplasty",
      "total disc replacement",
      "prodisc",
      "activl",
      "discogenic back pain",
      "lumbar tda",
    ],
  },
  {
    org: "NASS",
    document_title:
      "NASS Coverage Recommendation: Minimally Invasive Sacroiliac Joint Fusion",
    document_type: "Coverage Policy",
    year: 2021,
    canonical_url: "https://www.spine.org/Coverage",
    tags: [
      "sacroiliac joint",
      "si joint fusion",
      "sacroiliac fusion",
      "si joint dysfunction",
      "sacroiliitis",
      "minimally invasive si fusion",
      "ifuse",
      "si joint pain",
    ],
  },
  {
    org: "AOSpine",
    document_title:
      "AOSpine Thoracolumbar Spine Injury Classification System and Surgical Algorithm",
    document_type: "Classification System",
    year: 2013,
    canonical_url: "https://pubmed.ncbi.nlm.nih.gov/23970107/",
    tags: [
      "thoracolumbar fracture",
      "spinal trauma",
      "burst fracture",
      "thoracolumbar injury",
      "tl aosis",
      "spine fracture classification",
      "posterior ligamentous complex",
      "spinal cord injury",
      "thoracolumbar trauma",
    ],
  },
  {
    org: "SRS",
    document_title:
      "Scoliosis Research Society–Schwab Adult Spinal Deformity Classification",
    document_type: "Classification System",
    year: 2012,
    canonical_url: "https://pubmed.ncbi.nlm.nih.gov/22045006/",
    tags: [
      "adult spinal deformity",
      "adult scoliosis",
      "degenerative scoliosis",
      "sagittal imbalance",
      "sagittal vertical axis",
      "pelvic incidence",
      "lumbar lordosis",
      "spinal deformity correction",
      "scoliosis surgery",
      "kyphosis",
    ],
  },
  {
    org: "NASS",
    document_title:
      "NASS Clinical Guideline: Diagnosis and Treatment of Degenerative Lumbar Spondylolisthesis (2nd Edition)",
    document_type: "Clinical Practice Guideline",
    year: 2016,
    canonical_url: "https://pubmed.ncbi.nlm.nih.gov/26681351/",
    tags: [
      "degenerative spondylolisthesis",
      "lumbar spondylolisthesis",
      "grade i spondylolisthesis",
      "grade ii spondylolisthesis",
      "listhesis",
      "lumbar instability",
      "decompression versus fusion",
      "decompression with fusion",
      "posterolateral fusion",
      "interbody fusion",
      "plif",
      "tlif",
      "alif",
      "instrumented fusion",
      "facet joint effusion",
      "flexion extension radiographs",
      "dynamic instability",
      "sport trial",
      "lumbar slip",
      "pedicle screw fixation",
    ],
  },

  // --- AAOS ---
  {
    org: "AAOS",
    document_title:
      "AAOS Clinical Practice Guideline: Management of Osteoarthritis of the Knee",
    document_type: "Clinical Practice Guideline",
    year: 2021,
    canonical_url:
      "https://www.aaos.org/quality/quality-programs/osteoarthritis-of-the-knee/",
    tags: [
      "knee osteoarthritis",
      "total knee replacement",
      "viscosupplementation",
    ],
  },
  {
    org: "AAOS",
    document_title:
      "AAOS Clinical Practice Guideline: Management of Rotator Cuff Injuries",
    document_type: "Clinical Practice Guideline",
    year: 2019,
    canonical_url:
      "https://www.aaos.org/quality/quality-programs/upper-extremity-programs/rotator-cuff-injuries/",
    tags: ["rotator cuff", "rotator cuff tear", "shoulder arthroscopy"],
  },
  {
    org: "AAOS",
    document_title:
      "AAOS Clinical Practice Guideline: Management of ACL Injuries",
    document_type: "Clinical Practice Guideline",
    year: 2014,
    canonical_url:
      "https://www.aaos.org/quality/quality-programs/anterior-cruciate-ligament-injuries/",
    tags: ["acl", "acl tear", "acl reconstruction"],
  },
  {
    org: "AAOS",
    document_title:
      "AAOS Clinical Practice Guideline: Management of Carpal Tunnel Syndrome",
    document_type: "Clinical Practice Guideline",
    year: 2016,
    canonical_url:
      "https://www.aaos.org/quality/quality-programs/upper-extremity-programs/carpal-tunnel-syndrome/",
    tags: ["carpal tunnel", "nerve conduction", "emg"],
  },
  {
    org: "AAOS",
    document_title:
      "AAOS Clinical Practice Guideline: Management of Hip Fractures in Older Adults",
    document_type: "Clinical Practice Guideline",
    year: 2021,
    canonical_url:
      "https://www.aaos.org/quality/quality-programs/hip-fractures-in-older-adults/",
    tags: [
      "hip fracture",
      "femoral neck fracture",
      "intertrochanteric fracture",
      "hemiarthroplasty",
      "total hip arthroplasty",
      "cemented stem",
      "cephalomedullary nail",
      "surgical timing hip fracture",
      "geriatric fracture",
    ],
  },
  {
    org: "AAOS",
    document_title:
      "AAOS Clinical Practice Guideline: Management of Glenohumeral Joint Osteoarthritis",
    document_type: "Clinical Practice Guideline",
    year: 2020,
    canonical_url:
      "https://www.aaos.org/quality/quality-programs/glenohumeral-joint-osteoarthritis/",
    tags: [
      "shoulder arthritis",
      "glenohumeral osteoarthritis",
      "total shoulder arthroplasty",
      "reverse total shoulder arthroplasty",
      "shoulder replacement",
      "rotator cuff arthropathy",
    ],
  },
  {
    org: "AAOS",
    document_title:
      "AAOS Clinical Practice Guideline: Surgical Management of Osteoarthritis of the Knee",
    document_type: "Clinical Practice Guideline",
    year: 2022,
    canonical_url: "https://pubmed.ncbi.nlm.nih.gov/37883429/",
    tags: [
      "total knee arthroplasty",
      "tka",
      "total knee replacement",
      "tkr",
      "unicompartmental knee arthroplasty",
      "uka",
      "partial knee replacement",
      "knee replacement surgery",
      "surgical knee oa",
      "bmi knee replacement",
      "robotic knee arthroplasty",
      "revision tka",
      "tranexamic acid tka",
      "perioperative glucose",
      "knee oa surgical",
    ],
  },
  {
    org: "AAOS",
    document_title:
      "AAOS Clinical Practice Guideline: Management of Osteoarthritis of the Hip",
    document_type: "Clinical Practice Guideline",
    year: 2023,
    canonical_url: "https://pubmed.ncbi.nlm.nih.gov/38781351/",
    tags: [
      "hip osteoarthritis",
      "total hip arthroplasty",
      "tha",
      "total hip replacement",
      "thr",
      "hip replacement surgery",
      "hip oa",
      "bmi hip replacement",
      "anterior approach hip",
      "posterior approach hip",
      "cemented femoral stem",
      "cementless hip",
      "hip spine syndrome",
      "tranexamic acid tha",
      "intraarticular hip injection",
      "hip corticosteroid injection",
    ],
  },
  {
    org: "AAOS",
    document_title:
      "AAOS Clinical Practice Guideline: Diagnosis and Prevention of Periprosthetic Joint Infections",
    document_type: "Clinical Practice Guideline",
    year: 2019,
    canonical_url: "https://pubmed.ncbi.nlm.nih.gov/31972719/",
    tags: [
      "periprosthetic joint infection",
      "pji",
      "prosthetic joint infection",
      "revision arthroplasty",
      "revision tka",
      "revision tha",
      "infected joint replacement",
      "two-stage revision",
      "antibiotic spacer",
      "aspiration joint",
      "synovial fluid analysis",
      "alpha-defensin",
      "crp joint infection",
      "esr joint infection",
      "bmi pji risk",
      "surgical site infection arthroplasty",
    ],
  },
  {
    org: "AAOS/AAHKS",
    document_title:
      "AAOS/AAHKS Clinical Practice Guideline: Prevention of Periprosthetic Joint Infection in Patients Undergoing Dental Procedures",
    document_type: "Clinical Practice Guideline",
    year: 2024,
    canonical_url:
      "https://www.aaos.org/quality/quality-programs/clinical-practice-guidelines/",
    tags: [
      "dental prophylaxis arthroplasty",
      "antibiotic prophylaxis dental",
      "periprosthetic infection dental",
      "joint replacement dental procedure",
      "dental screening tja",
    ],
  },

  // --- GI ---
  {
    org: "ACG",
    document_title:
      "ACG Clinical Guideline: Management of Patients with Acute Lower GI Bleeding",
    document_type: "Clinical Practice Guideline",
    year: 2023,
    canonical_url:
      "https://journals.lww.com/ajg/fulltext/2023/01000/acg_clinical_guideline__management_of_patients_with.15.aspx",
    tags: ["lower gi bleed", "rectal bleeding", "hematochezia", "colonoscopy"],
  },
  {
    org: "ACG",
    document_title:
      "ACG Clinical Guideline: Upper Gastrointestinal and Ulcer Bleeding",
    document_type: "Clinical Practice Guideline",
    year: 2021,
    canonical_url:
      "https://journals.lww.com/ajg/fulltext/2021/05000/acg_clinical_guideline__upper_gastrointestinal_and.14.aspx",
    tags: ["upper gi bleed", "hematemesis", "peptic ulcer", "egd", "melena"],
  },
  {
    org: "ACG",
    document_title:
      "ACG Clinical Guideline: Management of Crohn's Disease in Adults",
    document_type: "Clinical Practice Guideline",
    year: 2018,
    canonical_url:
      "https://journals.lww.com/ajg/fulltext/2018/04000/acg_clinical_guideline__management_of_crohn_s.10.aspx",
    tags: ["crohn", "ibd", "humira", "remicade", "biologics"],
  },
  {
    org: "AGA",
    document_title: "AGA Clinical Practice Update: Barrett's Esophagus",
    document_type: "Clinical Practice Update",
    year: 2022,
    canonical_url:
      "https://www.gastrojournal.org/article/S0016-5085(22)00150-4/fulltext",
    tags: ["barrett", "esophageal ablation", "dysplasia", "egd"],
  },
  {
    org: "AGA",
    document_title: "AGA Clinical Practice Update on Management of GERD",
    document_type: "Clinical Practice Update",
    year: 2022,
    canonical_url:
      "https://www.gastrojournal.org/article/S0016-5085(21)03855-0/fulltext",
    tags: ["gerd", "reflux", "fundoplication", "ppi"],
  },
  {
    org: "ACG",
    document_title: "ACG Clinical Guideline: Ulcerative Colitis in Adults",
    document_type: "Clinical Practice Guideline",
    year: 2019,
    canonical_url: "https://pubmed.ncbi.nlm.nih.gov/30840605/",
    tags: [
      "ulcerative colitis",
      "ibd",
      "biologic therapy uc",
      "vedolizumab",
      "tofacitinib",
      "upadacitinib",
      "mesalamine",
      "colectomy",
      "proctocolectomy",
      "janus kinase inhibitor",
      "ozanimod",
    ],
  },
  {
    org: "ACG/AGA",
    document_title:
      "ACG/AGA Clinical Guideline: Management of Gallstones and Cholecystitis",
    document_type: "Clinical Practice Guideline",
    year: 2024,
    canonical_url: "https://pubmed.ncbi.nlm.nih.gov/39352025/",
    tags: [
      "gallstones",
      "cholecystitis",
      "cholecystectomy",
      "laparoscopic cholecystectomy",
      "choledocholithiasis",
      "ercp",
      "biliary colic",
      "common bile duct stone",
    ],
  },
  {
    org: "ACG",
    document_title:
      "ACG Clinical Guideline: Management of Irritable Bowel Syndrome",
    document_type: "Clinical Practice Guideline",
    year: 2021,
    canonical_url: "https://pubmed.ncbi.nlm.nih.gov/33315591/",
    tags: [
      "irritable bowel syndrome",
      "ibs",
      "ibs-d",
      "ibs-c",
      "rifaximin",
      "eluxadoline",
      "linaclotide",
      "low fodmap",
      "abdominal pain",
    ],
  },

  // --- AAN ---
  {
    org: "AAN",
    document_title: "AAN Practice Guideline: Migraine Prevention in Adults",
    document_type: "Clinical Practice Guideline",
    year: 2012,
    canonical_url: "https://www.aan.com/Guidelines/home/GuidelineDetail/536",
    tags: ["migraine", "migraine prevention", "cgrp", "botox"],
  },
  {
    org: "AAN",
    document_title:
      "AAN Practice Guideline: Disease-Modifying Therapies for Multiple Sclerosis",
    document_type: "Clinical Practice Guideline",
    year: 2018,
    canonical_url: "https://www.aan.com/Guidelines/home/GuidelineDetail/898",
    tags: ["multiple sclerosis", "ms", "ocrevus", "tysabri"],
  },
  {
    org: "AAN",
    document_title: "AAN Practice Guideline: Treatment of Diabetic Neuropathy",
    document_type: "Clinical Practice Guideline",
    year: 2022,
    canonical_url: "https://pubmed.ncbi.nlm.nih.gov/34965987/",
    tags: [
      "diabetic neuropathy",
      "painful neuropathy",
      "peripheral neuropathy",
      "pregabalin",
      "duloxetine",
      "gabapentin",
      "neuropathic pain",
      "nerve conduction study",
    ],
  },
  {
    org: "AAN",
    document_title:
      "AAN Practice Guideline: Disease-Modifying Therapies for Relapsing Multiple Sclerosis",
    document_type: "Clinical Practice Guideline",
    year: 2018,
    canonical_url: "https://pubmed.ncbi.nlm.nih.gov/29686116/",
    tags: [
      "multiple sclerosis",
      "relapsing ms",
      "disease modifying therapy",
      "ocrelizumab",
      "natalizumab",
      "fingolimod",
      "dimethyl fumarate",
      "ofatumumab",
      "high efficacy dmt",
    ],
  },
  {
    org: "MGFA/AAN",
    document_title:
      "International Consensus Guidance for the Management of Myasthenia Gravis",
    document_type: "Consensus Guideline",
    year: 2021,
    canonical_url: "https://pubmed.ncbi.nlm.nih.gov/34106382/",
    tags: [
      "myasthenia gravis",
      "mg",
      "acetylcholine receptor antibody",
      "thymectomy",
      "pyridostigmine",
      "ivig",
      "plasma exchange",
      "eculizumab",
      "efgartigimod",
      "rituximab mg",
      "myasthenic crisis",
    ],
  },
  {
    org: "AAN",
    document_title:
      "AAN Practice Guideline: Evaluation and Treatment of Functional Neurological Disorder",
    document_type: "Clinical Practice Guideline",
    year: 2024,
    canonical_url: "https://pubmed.ncbi.nlm.nih.gov/38408288/",
    tags: [
      "functional neurological disorder",
      "conversion disorder",
      "psychogenic nonepileptic seizures",
      "functional movement disorder",
      "functional seizures",
    ],
  },

  // --- PULMONOLOGY ---
  {
    org: "ATS",
    document_title: "ATS/ERS: COPD Diagnosis and Management",
    document_type: "Clinical Practice Guideline",
    year: 2017,
    canonical_url:
      "https://www.atsjournals.org/doi/full/10.1164/rccm.201701-0218PP",
    tags: ["copd", "emphysema", "bronchodilator", "spirometry"],
  },
  {
    org: "Fleischner Society",
    document_title:
      "Fleischner Society Guidelines for Management of Incidental Pulmonary Nodules",
    document_type: "Consensus Statement",
    year: 2017,
    canonical_url: "https://pubs.rsna.org/doi/10.1148/radiol.2017161659",
    tags: ["lung nodule", "pulmonary nodule", "fleischner", "ground glass"],
  },
  {
    org: "CHEST",
    document_title: "CHEST Guideline: Antithrombotic Therapy for VTE Disease",
    document_type: "Clinical Practice Guideline",
    year: 2016,
    canonical_url:
      "https://journal.chestnet.org/article/S0012-3692(15)00335-9/fulltext",
    tags: [
      "dvt",
      "pulmonary embolism",
      "anticoagulation",
      "eliquis",
      "xarelto",
      "vte",
    ],
  },
  {
    org: "ATS/ERS/JRS/ALAT",
    document_title:
      "ATS/ERS/JRS/ALAT Clinical Practice Guideline: Treatment of Idiopathic Pulmonary Fibrosis",
    document_type: "Clinical Practice Guideline",
    year: 2022,
    canonical_url: "https://pubmed.ncbi.nlm.nih.gov/35486072/",
    tags: [
      "idiopathic pulmonary fibrosis",
      "ipf",
      "nintedanib",
      "pirfenidone",
      "interstitial lung disease",
      "ild",
      "pulmonary fibrosis",
      "antifibrotic therapy",
      "lung transplant referral",
    ],
  },
  {
    org: "AASM",
    document_title:
      "AASM Clinical Practice Guideline: Treatment of Obstructive Sleep Apnea in Adults (PAP Therapy)",
    document_type: "Clinical Practice Guideline",
    year: 2019,
    canonical_url: "https://pubmed.ncbi.nlm.nih.gov/30736888/",
    tags: [
      "obstructive sleep apnea",
      "osa",
      "cpap",
      "bipap",
      "positive airway pressure",
      "auto-pap",
      "apap",
      "polysomnography",
      "home sleep testing",
      "sleep apnea surgery",
      "hypoglossal nerve stimulator",
      "inspire",
    ],
  },

  // --- SCREENING ---
  {
    org: "USPSTF",
    document_title:
      "USPSTF Recommendation Statement: Lung Cancer Screening with Low-Dose CT",
    document_type: "Recommendation Statement",
    year: 2021,
    canonical_url:
      "https://www.uspreventiveservicestaskforce.org/uspstf/recommendation/lung-cancer-screening",
    tags: ["lung cancer screening", "ldct", "low dose ct"],
  },
  {
    org: "USPSTF",
    document_title: "USPSTF Recommendation Statement: Breast Cancer Screening",
    document_type: "Recommendation Statement",
    year: 2024,
    canonical_url:
      "https://www.uspreventiveservicestaskforce.org/uspstf/recommendation/breast-cancer-screening",
    tags: ["breast cancer screening", "mammography", "mammogram"],
  },
  {
    org: "ACS",
    document_title: "ACS: Endometrial Cancer — Can It Be Found Early?",
    document_type: "Screening Guideline",
    year: 2024,
    canonical_url:
      "https://www.cancer.org/cancer/types/endometrial-cancer/detection-diagnosis-staging/detection.html",
    tags: [
      "endometrial cancer",
      "uterine cancer",
      "postmenopausal bleeding",
      "tvus",
      "vaginal bleeding",
    ],
  },
  {
    org: "USPSTF",
    document_title:
      "USPSTF Recommendation Statement: Screening for Colorectal Cancer",
    document_type: "Recommendation Statement",
    year: 2021,
    canonical_url:
      "https://www.uspreventiveservicestaskforce.org/uspstf/recommendation/colorectal-cancer-screening",
    tags: [
      "colorectal cancer screening",
      "colonoscopy",
      "cologuard",
      "fit test",
      "stool dna",
      "colon cancer screening",
      "ct colonography",
    ],
  },
  {
    org: "USPSTF",
    document_title:
      "USPSTF Recommendation Statement: Screening for Cervical Cancer",
    document_type: "Recommendation Statement",
    year: 2018,
    canonical_url:
      "https://www.uspreventiveservicestaskforce.org/uspstf/recommendation/cervical-cancer-screening",
    tags: [
      "cervical cancer screening",
      "pap smear",
      "hpv testing",
      "cervical cytology",
      "colposcopy",
    ],
  },

  // --- UROLOGY ---
  {
    org: "AUA",
    document_title: "AUA Guideline: Management of Benign Prostatic Hyperplasia",
    document_type: "Clinical Practice Guideline",
    year: 2021,
    canonical_url:
      "https://www.auanet.org/guidelines-and-quality/guidelines/benign-prostatic-hyperplasia-(bph)-guideline",
    tags: ["bph", "turp", "rezum", "urolift", "prostate enlargement"],
  },
  {
    org: "AUA",
    document_title:
      "AUA/Endourological Society Guideline: Surgical Management of Stones",
    document_type: "Clinical Practice Guideline",
    year: 2022,
    canonical_url:
      "https://www.auanet.org/guidelines-and-quality/guidelines/kidney-stones-surgical-management-guideline",
    tags: ["kidney stone", "nephrolithiasis", "ureteroscopy", "lithotripsy"],
  },
  {
    org: "AUA",
    document_title:
      "AUA/SUFU Guideline: Diagnosis and Treatment of Overactive Bladder (Non-Neurogenic) in Adults",
    document_type: "Clinical Practice Guideline",
    year: 2019,
    canonical_url:
      "https://www.auanet.org/guidelines-and-quality/guidelines/idiopathic-overactive-bladder",
    tags: [
      "overactive bladder",
      "oab",
      "urinary urgency",
      "urge incontinence",
      "anticholinergic",
      "mirabegron",
      "botox bladder",
      "onabotulinumtoxinA bladder",
      "sacral neuromodulation",
      "ptns",
      "vibegron",
    ],
  },
  {
    org: "AUA",
    document_title: "AUA Guideline: Early Detection of Prostate Cancer",
    document_type: "Clinical Practice Guideline",
    year: 2023,
    canonical_url:
      "https://www.auanet.org/guidelines-and-quality/guidelines/early-detection-of-prostate-cancer-guidelines",
    tags: [
      "prostate cancer screening",
      "psa",
      "prostate specific antigen",
      "prostate biopsy",
      "mri prostate",
      "pirads",
      "active surveillance prostate",
      "prostate cancer detection",
    ],
  },

  // --- ENDOCRINE ---
  {
    org: "ADA",
    document_title: "ADA Standards of Medical Care in Diabetes",
    document_type: "Standards of Care",
    year: 2024,
    canonical_url: "https://diabetesjournals.org/care/issue/47/Supplement_1",
    tags: [
      "diabetes",
      "type 2 diabetes",
      "ozempic",
      "semaglutide",
      "insulin pump",
      "cgm",
      "a1c",
    ],
  },
  {
    org: "ADA",
    document_title:
      "ADA Standards of Care: Obesity and Weight Management for Type 2 Diabetes",
    document_type: "Standards of Care",
    year: 2024,
    canonical_url:
      "https://diabetesjournals.org/care/article/47/Supplement_1/S145/153953",
    tags: ["obesity", "bariatric", "gastric bypass", "wegovy", "zepbound"],
  },
  {
    org: "Endocrine Society",
    document_title:
      "Endocrine Society Clinical Practice Guideline: Testosterone Therapy in Men With Hypogonadism",
    document_type: "Clinical Practice Guideline",
    year: 2018,
    canonical_url: "https://pubmed.ncbi.nlm.nih.gov/29562364/",
    tags: [
      "testosterone deficiency",
      "hypogonadism",
      "testosterone replacement therapy",
      "trt",
      "low testosterone",
      "androgen deficiency",
      "testosterone gel",
      "testosterone injection",
    ],
  },
  {
    org: "ATA",
    document_title:
      "ATA Management Guidelines for Adult Patients With Thyroid Nodules and Differentiated Thyroid Cancer",
    document_type: "Clinical Practice Guideline",
    year: 2015,
    canonical_url: "https://pubmed.ncbi.nlm.nih.gov/26462967/",
    tags: [
      "thyroid cancer",
      "differentiated thyroid cancer",
      "papillary thyroid cancer",
      "thyroid nodule",
      "thyroidectomy",
      "radioactive iodine",
      "rai",
      "fna biopsy thyroid",
      "bethesda classification",
      "thyroglobulin",
    ],
  },
  {
    org: "AACE/ACE",
    document_title:
      "AACE/ACE Clinical Practice Guidelines for the Diagnosis and Treatment of Postmenopausal Osteoporosis",
    document_type: "Clinical Practice Guideline",
    year: 2020,
    canonical_url: "https://pubmed.ncbi.nlm.nih.gov/32594155/",
    tags: [
      "osteoporosis",
      "postmenopausal osteoporosis",
      "bisphosphonate",
      "denosumab",
      "teriparatide",
      "romosozumab",
      "bone mineral density",
      "dexa scan",
      "frax score",
      "fracture prevention",
      "osteopenia",
    ],
  },

  // --- CMS ---
  {
    org: "CMS",
    document_title:
      "CMS NCD: Bariatric Surgery for Treatment of Morbid Obesity",
    document_type: "National Coverage Determination",
    year: 2006,
    canonical_url:
      "https://www.cms.gov/medicare-coverage-database/view/ncd.aspx?NCDId=57",
    tags: [
      "bariatric surgery",
      "gastric bypass",
      "gastric sleeve",
      "morbid obesity",
    ],
  },
  {
    org: "CMS",
    document_title: "CMS NCD: CPAP Therapy for Obstructive Sleep Apnea",
    document_type: "National Coverage Determination",
    year: 2008,
    canonical_url:
      "https://www.cms.gov/medicare-coverage-database/view/ncd.aspx?NCDId=300",
    tags: ["cpap", "bipap", "sleep apnea", "osa", "polysomnography"],
  },
  {
    org: "CMS",
    document_title: "CMS NCD: Electrical Nerve Stimulation",
    document_type: "National Coverage Determination",
    year: 2006,
    canonical_url:
      "https://www.cms.gov/medicare-coverage-database/view/ncd.aspx?NCDId=170",
    tags: ["spinal cord stimulator", "scs", "neuromodulation", "tens"],
  },
  {
    org: "CMS",
    document_title: "CMS NCD: Hyperbaric Oxygen Therapy",
    document_type: "National Coverage Determination",
    year: 2017,
    canonical_url:
      "https://www.cms.gov/medicare-coverage-database/view/ncd.aspx?NCDId=12",
    tags: [
      "hyperbaric oxygen therapy",
      "hbot",
      "wound healing",
      "diabetic wound",
      "chronic wound",
      "non-healing wound",
      "radiation injury",
      "gas gangrene",
    ],
  },
  {
    org: "CMS",
    document_title: "CMS NCD: Power Mobility Devices",
    document_type: "National Coverage Determination",
    year: 2006,
    canonical_url:
      "https://www.cms.gov/medicare-coverage-database/view/ncd.aspx?NCDId=219",
    tags: [
      "power wheelchair",
      "power mobility device",
      "scooter",
      "mobility assessment",
      "face to face evaluation",
      "durable medical equipment",
      "power operated vehicle",
    ],
  },
  {
    org: "CMS",
    document_title: "CMS NCD: Implantable Cardioverter Defibrillators",
    document_type: "National Coverage Determination",
    year: 2018,
    canonical_url:
      "https://www.cms.gov/medicare-coverage-database/view/ncd.aspx?NCDId=110",
    tags: [
      "implantable cardioverter defibrillator",
      "icd implant",
      "defibrillator",
      "sudden cardiac death",
      "primary prevention icd",
      "secondary prevention icd",
      "ejection fraction",
      "lvef",
      "ventricular tachycardia",
      "ventricular fibrillation",
      "cardiac arrest",
      "shared decision making icd",
      "ncd 20.4",
      "crt-d",
      "subcutaneous icd",
      "heart failure icd",
    ],
  },
  {
    org: "CMS",
    document_title: "CMS NCD: Cochlear Implantation",
    document_type: "National Coverage Determination",
    year: 2022,
    canonical_url:
      "https://www.cms.gov/medicare-coverage-database/view/ncd.aspx?NCDId=245",
    tags: [
      "cochlear implant",
      "cochlear implantation",
      "sensorineural hearing loss",
      "bilateral hearing loss",
      "moderate to profound hearing loss",
      "speech recognition",
      "auditory rehabilitation",
      "ncd 50.3",
      "hearing prosthetic",
      "limited benefit amplification",
    ],
  },
  {
    org: "CMS",
    document_title: "CMS NCD: FDG PET for Oncologic Conditions",
    document_type: "National Coverage Determination",
    year: 2013,
    canonical_url:
      "https://www.cms.gov/medicare-coverage-database/view/ncd.aspx?NCDId=331",
    tags: [
      "pet scan",
      "fdg pet",
      "pet ct",
      "oncologic pet",
      "initial treatment strategy",
      "subsequent treatment strategy",
      "cancer staging",
      "tumor restaging",
      "treatment monitoring pet",
      "ncd 220.6",
      "solid tumor pet",
      "lymphoma pet",
      "melanoma pet",
      "lung cancer pet",
      "breast cancer pet",
    ],
  },
  {
    org: "CMS",
    document_title: "CMS NCD: Cardiac Rehabilitation Programs",
    document_type: "National Coverage Determination",
    year: 2010,
    canonical_url:
      "https://www.cms.gov/medicare-coverage-database/view/ncd.aspx?NCDId=322",
    tags: [
      "cardiac rehabilitation",
      "cardiac rehab",
      "cr program",
      "intensive cardiac rehab",
      "icr",
      "post mi rehab",
      "post cabg rehab",
      "heart failure rehab",
      "stable angina",
      "heart valve repair rehab",
      "pci rehab",
      "coronary angioplasty rehab",
      "ncd 20.10",
    ],
  },
  {
    org: "CMS",
    document_title: "CMS NCD: Transcatheter Aortic Valve Replacement",
    document_type: "National Coverage Determination",
    year: 2019,
    canonical_url:
      "https://www.cms.gov/medicare-coverage-database/view/ncd.aspx?NCDId=355",
    tags: [
      "tavr",
      "tavi",
      "transcatheter aortic valve replacement",
      "transcatheter aortic valve implantation",
      "aortic stenosis",
      "severe aortic stenosis",
      "inoperable aortic stenosis",
      "high risk aortic stenosis",
      "heart valve replacement",
      "structural heart",
      "heart team",
      "ncd 20.32",
    ],
  },
  {
    org: "CMS",
    document_title: "CMS NCD: Ventricular Assist Devices",
    document_type: "National Coverage Determination",
    year: 2013,
    canonical_url:
      "https://www.cms.gov/medicare-coverage-database/view/ncd.aspx?NCDId=298",
    tags: [
      "ventricular assist device",
      "vad",
      "lvad",
      "left ventricular assist device",
      "destination therapy",
      "bridge to transplant",
      "mechanical circulatory support",
      "heart failure lvad",
      "end stage heart failure",
      "ncd 20.9",
    ],
  },
  {
    org: "CMS",
    document_title:
      "CMS NCD: Screening for Lung Cancer with Low Dose Computed Tomography",
    document_type: "National Coverage Determination",
    year: 2022,
    canonical_url:
      "https://www.cms.gov/medicare-coverage-database/view/ncd.aspx?NCDId=364",
    tags: [
      "lung cancer screening",
      "ldct screening",
      "low dose ct",
      "lung cancer early detection",
      "smoking history",
      "pack year",
      "shared decision making lung screening",
      "ncd 210.7",
      "annual lung screening",
    ],
  },
  {
    org: "CMS",
    document_title: "CMS NCD: Bone Mass Measurement",
    document_type: "National Coverage Determination",
    year: 2007,
    canonical_url:
      "https://www.cms.gov/medicare-coverage-database/view/ncd.aspx?NCDId=256",
    tags: [
      "bone density",
      "dexa scan",
      "bone mass measurement",
      "osteoporosis screening",
      "dual energy xray absorptiometry",
      "bone mineral density",
      "ncd 150.3",
      "fragility fracture risk",
      "postmenopausal osteoporosis screening",
    ],
  },
  {
    org: "CMS",
    document_title:
      "CMS NCD: Blood Glucose Monitors / Continuous Glucose Monitoring",
    document_type: "National Coverage Determination",
    year: 2017,
    canonical_url:
      "https://www.cms.gov/medicare-coverage-database/view/ncd.aspx?NCDId=109",
    tags: [
      "continuous glucose monitor",
      "cgm",
      "blood glucose monitor",
      "dexcom",
      "freestyle libre",
      "glucose monitoring",
      "diabetes monitoring",
      "therapeutic cgm",
      "insulin pump cgm",
      "ncd 40.2",
      "durable medical equipment diabetes",
    ],
  },
  {
    org: "CMS",
    document_title: "CMS NCD: Home Use of Oxygen",
    document_type: "National Coverage Determination",
    year: 2006,
    canonical_url:
      "https://www.cms.gov/medicare-coverage-database/view/ncd.aspx?NCDId=169",
    tags: [
      "home oxygen therapy",
      "supplemental oxygen",
      "oxygen concentrator",
      "portable oxygen",
      "chronic hypoxemia",
      "copd oxygen",
      "pulse oximetry",
      "arterial blood gas",
      "oxygen saturation",
      "ncd 240.2",
      "home oxygen qualifying criteria",
      "oxygen pao2",
    ],
  },
  {
    org: "CMS",
    document_title: "CMS NCD: Routine Costs in Clinical Trials",
    document_type: "National Coverage Determination",
    year: 2007,
    canonical_url:
      "https://www.cms.gov/medicare-coverage-database/view/ncd.aspx?NCDId=1",
    tags: [
      "clinical trial coverage",
      "routine costs clinical trial",
      "investigational treatment",
      "clinical trial medicare",
      "coverage with evidence development",
      "ced",
      "ncd 310.1",
      "qualifying clinical trial",
      "category b ide",
      "fda approved trial",
    ],
  },
  {
    org: "CMS",
    document_title: "CMS NCD: Transcatheter Mitral Valve Repair",
    document_type: "National Coverage Determination",
    year: 2014,
    canonical_url:
      "https://www.cms.gov/medicare-coverage-database/view/ncd.aspx?NCDId=356",
    tags: [
      "transcatheter mitral valve repair",
      "mitraclip",
      "tmvr",
      "mitral regurgitation",
      "severe mitral regurgitation",
      "functional mitral regurgitation",
      "degenerative mitral regurgitation",
      "structural heart",
      "heart team",
      "ncd 20.33",
    ],
  },

  // --- PAIN MEDICINE / INTERVENTIONAL PAIN ---
  {
    org: "ASIPP",
    document_title:
      "ASIPP Comprehensive Evidence-Based Guidelines: Epidural Interventions in Chronic Spinal Pain",
    document_type: "Clinical Practice Guideline",
    year: 2021,
    canonical_url: "https://pubmed.ncbi.nlm.nih.gov/33492918/",
    tags: [
      "epidural steroid injection",
      "esi",
      "interlaminar",
      "transforaminal",
      "caudal epidural",
      "lumbar radiculopathy",
      "cervical radiculopathy",
      "percutaneous adhesiolysis",
    ],
  },
  {
    org: "ASIPP",
    document_title:
      "ASIPP Comprehensive Evidence-Based Guidelines: Facet Joint Interventions in Chronic Spinal Pain",
    document_type: "Clinical Practice Guideline",
    year: 2020,
    canonical_url: "https://pubmed.ncbi.nlm.nih.gov/32503359/",
    tags: [
      "facet joint",
      "medial branch block",
      "radiofrequency ablation",
      "radiofrequency neurotomy",
      "facet joint injection",
      "axial back pain",
    ],
  },
  {
    org: "ASIPP",
    document_title:
      "ASIPP Comprehensive Evidence-Based Guidelines: Implantable Peripheral Nerve Stimulation (PNS) in Chronic Pain",
    document_type: "Clinical Practice Guideline",
    year: 2024,
    canonical_url: "https://pubmed.ncbi.nlm.nih.gov/39565237/",
    tags: [
      "peripheral nerve stimulation",
      "pns",
      "neuromodulation",
      "chronic pain",
      "neuropathic pain",
    ],
  },
  {
    org: "ASRA",
    document_title:
      "ASRA/ESRA/NANS Evidence-Based Consensus Guidelines: Patient Selection and Trial Stimulation for Spinal Cord Stimulation",
    document_type: "Consensus Guideline",
    year: 2023,
    canonical_url: "https://pmc.ncbi.nlm.nih.gov/articles/PMC10370290/",
    tags: [
      "spinal cord stimulator",
      "scs",
      "scs trial",
      "failed back surgery syndrome",
      "neuromodulation",
      "dorsal column stimulation",
    ],
  },
  {
    org: "CDC",
    document_title:
      "CDC Clinical Practice Guideline for Prescribing Opioids for Pain — United States, 2022",
    document_type: "Clinical Practice Guideline",
    year: 2022,
    canonical_url: "https://pubmed.ncbi.nlm.nih.gov/36327391/",
    tags: [
      "opioid prescribing",
      "opioid therapy",
      "chronic pain",
      "acute pain",
      "opioid dosing",
      "opioid tapering",
      "nonopioid treatment",
      "multimodal pain management",
      "opioid use disorder",
      "prescription monitoring",
    ],
  },
  {
    org: "PACC/INS",
    document_title:
      "Polyanalgesic Consensus Conference (PACC): Intrathecal Drug Delivery Guidance on Safety and Therapy Optimization — Chronic Noncancer Pain",
    document_type: "Consensus Guideline",
    year: 2024,
    canonical_url: "https://pubmed.ncbi.nlm.nih.gov/38752946/",
    tags: [
      "intrathecal drug delivery",
      "intrathecal pump",
      "it pump",
      "baclofen pump",
      "ziconotide",
      "intrathecal morphine",
      "targeted drug delivery",
      "programmable pump",
      "chronic pain",
    ],
  },
  {
    org: "ASPN",
    document_title:
      "ASPN Evidence-Based Clinical Guideline of Interventional Treatments for Low Back Pain",
    document_type: "Clinical Practice Guideline",
    year: 2022,
    canonical_url: "https://pubmed.ncbi.nlm.nih.gov/36510616/",
    tags: [
      "interventional low back pain",
      "lumbar epidural",
      "trigger point injection",
      "intradiscal therapy",
      "minimally invasive lumbar decompression",
      "basivertebral nerve ablation",
      "vertebroplasty",
      "lumbar radiofrequency ablation",
      "back pain intervention",
    ],
  },
  {
    org: "ASPN",
    document_title:
      "ASPN LEARN: Latest Evidence-Based Application for Radiofrequency Neurotomy — Best Practice Guidelines",
    document_type: "Best Practice Guideline",
    year: 2021,
    canonical_url: "https://pubmed.ncbi.nlm.nih.gov/34526815/",
    tags: [
      "radiofrequency neurotomy",
      "radiofrequency ablation",
      "rfa",
      "cooled radiofrequency",
      "genicular nerve ablation",
      "genicular nerve block",
      "knee denervation",
      "knee osteoarthritis rfa",
      "hip denervation",
      "si joint rfa",
      "pulsed radiofrequency",
      "peripheral joint denervation",
      "knee pain",
      "cooled rfa knee",
      "failed knee replacement pain",
      "occipital neuralgia rfa",
    ],
  },
  {
    org: "ASPN",
    document_title:
      "ASPN Best Practices for Dorsal Root Ganglion Stimulation for Chronic Pain",
    document_type: "Best Practice Guideline",
    year: 2023,
    canonical_url: "https://pubmed.ncbi.nlm.nih.gov/36942306/",
    tags: [
      "dorsal root ganglion stimulation",
      "drg stimulation",
      "drg-s",
      "complex regional pain syndrome",
      "crps",
      "causalgia",
      "neuromodulation",
      "focal pain",
      "neuropathic pain",
    ],
  },
  {
    org: "ASPN",
    document_title:
      "ASPN SWEET Guideline: Evidence, Education, and Treatment Algorithm for Painful Diabetic Neuropathy",
    document_type: "Clinical Practice Guideline",
    year: 2024,
    canonical_url: "https://pubmed.ncbi.nlm.nih.gov/38633823/",
    tags: [
      "painful diabetic neuropathy",
      "diabetic neuropathy",
      "pdn",
      "peripheral neuropathy",
      "spinal cord stimulation",
      "neuropathic pain",
      "diabetes pain",
      "neuromodulation",
    ],
  },
  {
    org: "ASPN",
    document_title:
      "ASPN Best Practice Guideline for the Treatment of Sacroiliac Disorders",
    document_type: "Best Practice Guideline",
    year: 2024,
    canonical_url: "https://pubmed.ncbi.nlm.nih.gov/38716038/",
    tags: [
      "sacroiliac joint",
      "si joint",
      "sij",
      "sacroiliitis",
      "si joint injection",
      "si joint radiofrequency ablation",
      "si joint fusion",
      "minimally invasive si joint fusion",
      "sacroiliac dysfunction",
      "lateral sacral branch",
      "si joint block",
      "si joint denervation",
      "posterior pelvic pain",
      "low back pain sacroiliac",
    ],
  },
  {
    org: "ACR/Arthritis Foundation",
    document_title:
      "2019 ACR/Arthritis Foundation Guideline for the Management of Osteoarthritis of the Hand, Hip, and Knee",
    document_type: "Clinical Practice Guideline",
    year: 2020,
    canonical_url: "https://pubmed.ncbi.nlm.nih.gov/31908149/",
    tags: [
      "osteoarthritis",
      "knee osteoarthritis",
      "hip osteoarthritis",
      "hand osteoarthritis",
      "radiofrequency ablation knee",
      "genicular nerve rfa",
      "intra-articular corticosteroid",
      "hyaluronic acid injection",
      "nsaid",
      "duloxetine",
      "knee pain management",
      "physical therapy osteoarthritis",
      "tai chi",
      "knee oa conservative",
      "kellgren lawrence",
    ],
  },
  {
    org: "RSDSA",
    document_title:
      "Complex Regional Pain Syndrome: Practical Diagnostic and Treatment Guidelines, 5th Edition",
    document_type: "Clinical Practice Guideline",
    year: 2022,
    canonical_url: "https://pubmed.ncbi.nlm.nih.gov/35687369/",
    tags: [
      "complex regional pain syndrome",
      "crps",
      "reflex sympathetic dystrophy",
      "rsd",
      "causalgia",
      "budapest criteria",
      "sympathetic nerve block",
      "stellate ganglion block",
      "ketamine infusion",
      "spinal cord stimulation crps",
      "drg stimulation crps",
      "mirror therapy",
      "graded motor imagery",
      "allodynia",
      "sudomotor",
      "trophic changes",
      "vitamin c prevention",
      "intrathecal baclofen",
      "functional restoration",
    ],
  },
  {
    org: "AHS",
    document_title:
      "AHS Consensus Statement: Update on Integrating New Migraine Treatments into Clinical Practice",
    document_type: "Consensus Statement",
    year: 2021,
    canonical_url: "https://pubmed.ncbi.nlm.nih.gov/34160823/",
    tags: [
      "chronic migraine",
      "migraine prevention",
      "onabotulinumtoxinA",
      "botox migraine",
      "botulinum toxin headache",
      "cgrp monoclonal antibody",
      "erenumab",
      "fremanezumab",
      "galcanezumab",
      "eptinezumab",
      "gepant",
      "atogepant",
      "rimegepant",
      "migraine prophylaxis",
      "preempt protocol",
      "headache days",
      "acute migraine treatment",
      "triptan",
      "medication overuse headache",
    ],
  },
  {
    org: "AHS",
    document_title:
      "AHS Position Statement Update: CGRP-Targeting Therapies Are a First-Line Option for the Prevention of Migraine",
    document_type: "Position Statement",
    year: 2024,
    canonical_url: "https://pubmed.ncbi.nlm.nih.gov/38466028/",
    tags: [
      "cgrp first line",
      "migraine prevention first line",
      "cgrp monoclonal antibody",
      "anti-cgrp",
      "erenumab",
      "fremanezumab",
      "galcanezumab",
      "eptinezumab",
      "atogepant",
      "rimegepant",
      "migraine prophylaxis",
      "step therapy migraine",
      "prior authorization migraine",
      "chronic migraine",
      "episodic migraine",
      "candesartan",
    ],
  },

  // --- NEUROSURGERY ---
  {
    org: "AANS/CNS",
    document_title:
      "AANS/CNS Joint Section Guideline: Surgical Management of Cervical Degenerative Disease",
    document_type: "Clinical Practice Guideline",
    year: 2009,
    canonical_url:
      "https://www.cns.org/guidelines/browse-guidelines-detail/surgical-management-of-cervical-degenerative-disea",
    tags: [
      "cervical myelopathy",
      "cervical spondylotic myelopathy",
      "acdf",
      "laminectomy",
      "laminoplasty",
      "cervical fusion",
      "cervical degenerative disease",
    ],
  },
  {
    org: "AOSpine/AANS/CNS",
    document_title:
      "Clinical Practice Guideline: Management of Degenerative Cervical Myelopathy",
    document_type: "Clinical Practice Guideline",
    year: 2017,
    canonical_url: "https://pubmed.ncbi.nlm.nih.gov/29164035/",
    tags: [
      "degenerative cervical myelopathy",
      "cervical spondylotic myelopathy",
      "csm",
      "cervical cord compression",
      "surgical decompression",
      "acdf",
      "anterior cervical discectomy fusion",
      "laminoplasty",
      "laminectomy cervical",
      "myelopathy severity",
      "modified japanese orthopaedic association",
      "mjoa",
      "cord signal change",
      "t2 hyperintensity",
      "neurological deterioration",
      "gait disturbance",
      "hand clumsiness",
      "hoffmann sign",
      "cervical stenosis myelopathy",
      "posterior cervical fusion",
    ],
  },
  {
    org: "AHA/ASA",
    document_title:
      "AHA/ASA Guideline: Management of Patients with Unruptured Intracranial Aneurysms",
    document_type: "Clinical Practice Guideline",
    year: 2015,
    canonical_url:
      "https://www.ahajournals.org/doi/10.1161/str.0000000000000070",
    tags: [
      "intracranial aneurysm",
      "unruptured aneurysm",
      "cerebral aneurysm",
      "coiling",
      "clipping",
      "subarachnoid hemorrhage",
    ],
  },
  {
    org: "AHA/ASA",
    document_title:
      "AHA/ASA Guideline: Prevention of Stroke in Patients with Stroke and TIA (incl. Carotid Revascularization)",
    document_type: "Clinical Practice Guideline",
    year: 2021,
    canonical_url:
      "https://www.ahajournals.org/doi/10.1161/STR.0000000000000375",
    tags: [
      "carotid endarterectomy",
      "carotid stenting",
      "carotid stenosis",
      "stroke prevention",
      "tia",
      "secondary stroke prevention",
    ],
  },
  {
    org: "AHA/ASA",
    document_title:
      "AHA/ASA Guideline: Management of Aneurysmal Subarachnoid Hemorrhage",
    document_type: "Clinical Practice Guideline",
    year: 2023,
    canonical_url:
      "https://www.ahajournals.org/doi/10.1161/STR.0000000000000436",
    tags: [
      "subarachnoid hemorrhage",
      "aneurysmal sah",
      "cerebral vasospasm",
      "aneurysm rupture",
      "hunt hess",
      "fisher grade",
    ],
  },
  {
    org: "BTF/AANS/CNS",
    document_title:
      "Guidelines for the Management of Severe Traumatic Brain Injury, 4th Edition",
    document_type: "Clinical Practice Guideline",
    year: 2016,
    canonical_url: "https://pubmed.ncbi.nlm.nih.gov/27654000/",
    tags: [
      "traumatic brain injury",
      "tbi",
      "severe tbi",
      "intracranial pressure",
      "icp monitoring",
      "decompressive craniectomy",
      "cerebral perfusion pressure",
      "gcs",
      "brain herniation",
      "hyperosmolar therapy",
    ],
  },
  {
    org: "AANS/CNS",
    document_title:
      "AANS/CNS Systematic Review and Evidence-Based Guidelines: Treatment of Adults with Metastatic Brain Tumors",
    document_type: "Clinical Practice Guideline",
    year: 2019,
    canonical_url:
      "https://www.cns.org/guidelines/treatment-adults-metastatic-brain-tumors/guidelines-treatment-of-adults-with-metastatic-bra-2",
    tags: [
      "brain metastases",
      "metastatic brain tumor",
      "stereotactic radiosurgery",
      "srs",
      "whole brain radiation",
      "wbrt",
      "craniotomy",
      "brain tumor resection",
      "radiation necrosis",
    ],
  },
  {
    org: "AAN/AES/AANS",
    document_title:
      "Practice Parameter: Temporal Lobe and Localized Neocortical Resections for Epilepsy",
    document_type: "Clinical Practice Guideline",
    year: 2003,
    canonical_url: "https://pubmed.ncbi.nlm.nih.gov/12601090/",
    tags: [
      "epilepsy surgery",
      "temporal lobe epilepsy",
      "drug resistant epilepsy",
      "refractory epilepsy",
      "temporal lobectomy",
      "seizure freedom",
      "epilepsy surgery referral",
      "mesial temporal sclerosis",
    ],
  },
  {
    org: "Japanese Society of NPH",
    document_title:
      "Guidelines for Management of Idiopathic Normal Pressure Hydrocephalus, 3rd Edition",
    document_type: "Clinical Practice Guideline",
    year: 2021,
    canonical_url: "https://pubmed.ncbi.nlm.nih.gov/33455998/",
    tags: [
      "normal pressure hydrocephalus",
      "nph",
      "inph",
      "ventriculoperitoneal shunt",
      "vp shunt",
      "gait disturbance",
      "ventriculomegaly",
      "desh",
      "csf tap test",
      "programmable shunt",
    ],
  },
  {
    org: "MDS",
    document_title:
      "International Parkinson and Movement Disorder Society Evidence-Based Medicine Review: Treatments for Motor Symptoms of Parkinson's Disease",
    document_type: "Evidence-Based Medicine Review",
    year: 2018,
    canonical_url: "https://pubmed.ncbi.nlm.nih.gov/29570866/",
    tags: [
      "parkinson disease",
      "deep brain stimulation",
      "dbs",
      "subthalamic nucleus",
      "globus pallidus",
      "motor fluctuations",
      "levodopa",
      "dopamine agonist",
      "advanced parkinson therapy",
    ],
  },
  {
    org: "NASS",
    document_title:
      "NASS Evidence-Based Clinical Guideline: Diagnosis and Treatment of Osteoporotic Vertebral Compression Fractures",
    document_type: "Clinical Practice Guideline",
    year: 2024,
    canonical_url: "https://pubmed.ncbi.nlm.nih.gov/39894268/",
    tags: [
      "vertebral compression fracture",
      "osteoporotic fracture",
      "vertebroplasty",
      "kyphoplasty",
      "vertebral augmentation",
      "acute fracture",
      "chronic fracture",
      "spinal fracture",
    ],
  },
  {
    org: "AANS/CNS",
    document_title:
      "Congress of Neurological Surgeons Systematic Review and Evidence-Based Guidelines: Chiari Malformation Type I",
    document_type: "Clinical Practice Guideline",
    year: 2023,
    canonical_url:
      "https://www.cns.org/guidelines/browse-guidelines-detail/3-surgical-interventions",
    tags: [
      "chiari malformation",
      "chiari type 1",
      "posterior fossa decompression",
      "duraplasty",
      "syringomyelia",
      "cerebellar tonsillar ectopia",
      "foramen magnum decompression",
    ],
  },
  {
    org: "AHA/ASA",
    document_title:
      "AHA/ASA Guideline: Management of Spontaneous Intracerebral Hemorrhage",
    document_type: "Clinical Practice Guideline",
    year: 2022,
    canonical_url:
      "https://www.ahajournals.org/doi/10.1161/STR.0000000000000407",
    tags: [
      "intracerebral hemorrhage",
      "ich",
      "spontaneous hemorrhage",
      "hypertensive hemorrhage",
      "cerebral amyloid angiopathy",
      "hematoma expansion",
      "anticoagulation reversal",
      "blood pressure management",
      "surgical evacuation",
    ],
  },
  {
    org: "EAN",
    document_title:
      "European Academy of Neurology Guideline on Trigeminal Neuralgia",
    document_type: "Clinical Practice Guideline",
    year: 2019,
    canonical_url: "https://pubmed.ncbi.nlm.nih.gov/30860637/",
    tags: [
      "trigeminal neuralgia",
      "tic douloureux",
      "microvascular decompression",
      "mvd",
      "carbamazepine",
      "oxcarbazepine",
      "gamma knife",
      "radiofrequency rhizotomy",
      "glycerol rhizolysis",
      "balloon compression",
    ],
  },
  {
    org: "Endocrine Society",
    document_title:
      "Endocrine Society Clinical Practice Guideline: Pituitary Incidentaloma — An Endocrine Society Clinical Practice Guideline",
    document_type: "Clinical Practice Guideline",
    year: 2011,
    canonical_url: "https://pmc.ncbi.nlm.nih.gov/articles/PMC5393422/",
    tags: [
      "pituitary incidentaloma",
      "pituitary adenoma",
      "sellar mass",
      "transsphenoidal surgery",
      "hyperprolactinemia",
      "acromegaly",
      "cushing disease",
      "nonfunctioning adenoma",
      "visual field defect",
      "pituitary apoplexy",
    ],
  },
  {
    org: "EANO",
    document_title:
      "EANO Guidelines on the Diagnosis and Treatment of Diffuse Gliomas of Adulthood",
    document_type: "Clinical Practice Guideline",
    year: 2021,
    canonical_url: "https://pubmed.ncbi.nlm.nih.gov/33293629/",
    tags: [
      "glioma",
      "glioblastoma",
      "astrocytoma",
      "oligodendroglioma",
      "idh mutant",
      "idh wildtype",
      "temozolomide",
      "brain tumor",
      "gbm",
      "mgmt",
      "1p19q",
      "diffuse glioma",
      "tumor treating fields",
      "bevacizumab",
      "recurrent glioblastoma",
    ],
  },
  {
    org: "European Expert Consensus",
    document_title:
      "European Expert Consensus on Improving Patient Selection for the Management of Disabling Spasticity with Intrathecal Baclofen and/or Botulinum Toxin Type A",
    document_type: "Consensus Statement",
    year: 2022,
    canonical_url: "https://pubmed.ncbi.nlm.nih.gov/34608495/",
    tags: [
      "intrathecal baclofen",
      "spasticity",
      "baclofen pump",
      "botulinum toxin",
      "itb",
      "severe spasticity",
      "spastic hypertonia",
      "cerebral palsy spasticity",
      "multiple sclerosis spasticity",
      "spinal cord injury spasticity",
      "stroke spasticity",
      "generalized spasticity",
    ],
  },
  {
    org: "AANS/CNS",
    document_title:
      "Congress of Neurological Surgeons Systematic Review and Evidence-Based Guidelines on the Treatment of Adults with Vestibular Schwannomas",
    document_type: "Clinical Practice Guideline",
    year: 2018,
    canonical_url: "https://pubmed.ncbi.nlm.nih.gov/29309649/",
    tags: [
      "vestibular schwannoma",
      "acoustic neuroma",
      "cerebellopontine angle",
      "hearing preservation",
      "facial nerve",
      "stereotactic radiosurgery",
      "gamma knife",
      "retrosigmoid approach",
      "translabyrinthine",
      "middle fossa approach",
      "koos grade",
      "observation",
      "watch and wait",
      "srs vs surgery",
      "intraoperative monitoring",
    ],
  },
  {
    org: "EANO",
    document_title:
      "EANO Guideline on the Diagnosis and Management of Meningiomas",
    document_type: "Clinical Practice Guideline",
    year: 2021,
    canonical_url: "https://pubmed.ncbi.nlm.nih.gov/34181733/",
    tags: [
      "meningioma",
      "atypical meningioma",
      "anaplastic meningioma",
      "who grade 2 meningioma",
      "who grade 3 meningioma",
      "skull base meningioma",
      "simpson grade",
      "gross total resection",
      "meningioma recurrence",
      "observation meningioma",
      "incidental meningioma",
      "fractionated radiotherapy",
      "radiosurgery meningioma",
      "convexity meningioma",
      "parasagittal meningioma",
      "molecular profiling meningioma",
    ],
  },

  // --- ONCOLOGY ---
  {
    org: "CAP/IASLC/AMP",
    document_title:
      "CAP/IASLC/AMP Updated Molecular Testing Guideline for the Selection of Lung Cancer Patients for Treatment With Targeted Tyrosine Kinase Inhibitors",
    document_type: "Clinical Practice Guideline",
    year: 2018,
    canonical_url: "https://pubmed.ncbi.nlm.nih.gov/29355391/",
    tags: [
      "nsclc",
      "non-small cell lung cancer",
      "molecular testing",
      "egfr",
      "alk",
      "ros1",
      "braf",
      "kras",
      "met",
      "ret",
      "ntrk",
      "next generation sequencing",
      "ngs",
      "biomarker testing",
      "liquid biopsy",
      "tyrosine kinase inhibitor",
      "lung adenocarcinoma",
      "pd-l1",
    ],
  },
  {
    org: "ASCO",
    document_title:
      "ASCO Living Guideline: Therapy for Stage IV Non-Small-Cell Lung Cancer With Driver Alterations",
    document_type: "Living Guideline",
    year: 2022,
    canonical_url: "https://pubmed.ncbi.nlm.nih.gov/35816666/",
    tags: [
      "nsclc",
      "non-small cell lung cancer",
      "driver alteration",
      "egfr mutation",
      "alk rearrangement",
      "ros1",
      "braf v600e",
      "met exon 14",
      "ret rearrangement",
      "ntrk fusion",
      "osimertinib",
      "alectinib",
      "lorlatinib",
      "targeted therapy",
      "stage iv lung cancer",
    ],
  },
  {
    org: "ASCO",
    document_title:
      "ASCO Living Guideline: Therapy for Stage IV Non-Small-Cell Lung Cancer Without Driver Alterations",
    document_type: "Living Guideline",
    year: 2024,
    canonical_url: "https://pubmed.ncbi.nlm.nih.gov/38417098/",
    tags: [
      "nsclc",
      "non-small cell lung cancer",
      "immunotherapy",
      "pembrolizumab",
      "nivolumab",
      "atezolizumab",
      "cemiplimab",
      "pd-l1",
      "checkpoint inhibitor",
      "chemoimmunotherapy",
      "stage iv lung cancer",
      "no driver alteration",
      "carboplatin",
      "pemetrexed",
    ],
  },
  {
    org: "ASCO",
    document_title:
      "ASCO Guideline Update: Management of Immune-Related Adverse Events in Patients Treated With Immune Checkpoint Inhibitor Therapy",
    document_type: "Clinical Practice Guideline",
    year: 2021,
    canonical_url: "https://pubmed.ncbi.nlm.nih.gov/34724392/",
    tags: [
      "immune related adverse events",
      "irae",
      "checkpoint inhibitor toxicity",
      "immunotherapy side effects",
      "colitis",
      "hepatitis",
      "pneumonitis",
      "thyroiditis",
      "myocarditis",
      "corticosteroid",
      "infliximab",
      "immunosuppression",
      "nivolumab",
      "pembrolizumab",
      "ipilimumab",
      "grade 3 toxicity",
    ],
  },
  {
    org: "ASCO",
    document_title: "ASCO Guideline Update: Systemic Therapy for Melanoma",
    document_type: "Clinical Practice Guideline",
    year: 2023,
    canonical_url: "https://pubmed.ncbi.nlm.nih.gov/37579248/",
    tags: [
      "melanoma",
      "immunotherapy",
      "braf inhibitor",
      "mek inhibitor",
      "dabrafenib",
      "trametinib",
      "nivolumab",
      "pembrolizumab",
      "ipilimumab",
      "neoadjuvant immunotherapy",
      "adjuvant melanoma",
      "stage iib",
      "stage iii melanoma",
      "stage iv melanoma",
      "cutaneous melanoma",
    ],
  },
  {
    org: "ASCO/SSO",
    document_title:
      "ASCO/SSO Guideline: Germline Testing in Patients With Breast Cancer",
    document_type: "Clinical Practice Guideline",
    year: 2024,
    canonical_url: "https://pubmed.ncbi.nlm.nih.gov/38175972/",
    tags: [
      "brca1",
      "brca2",
      "germline testing",
      "genetic testing breast cancer",
      "hereditary breast cancer",
      "parp inhibitor",
      "olaparib",
      "multigene panel",
      "genetic counseling",
      "palb2",
      "chek2",
      "atm",
      "tp53",
      "li-fraumeni",
      "hereditary cancer",
    ],
  },
  {
    org: "ACMG/NSGC",
    document_title:
      "ACMG/NSGC Practice Guideline: Referral Indications for Cancer Predisposition Assessment",
    document_type: "Practice Guideline",
    year: 2015,
    canonical_url: "https://pubmed.ncbi.nlm.nih.gov/25394175/",
    tags: [
      "cancer predisposition",
      "genetic counseling referral",
      "hereditary cancer syndrome",
      "lynch syndrome",
      "brca",
      "li-fraumeni",
      "cowden syndrome",
      "familial adenomatous polyposis",
      "hereditary breast ovarian cancer",
      "genetic risk assessment",
      "cancer genetics",
    ],
  },
  {
    org: "ASTRO",
    document_title: "ASTRO Model Policy: Proton Beam Therapy",
    document_type: "Model Policy",
    year: 2023,
    canonical_url:
      "https://www.astro.org/astro/media/astro/daily%20practice/pdfs/astropbtmodelpolicy.pdf",
    tags: [
      "proton beam therapy",
      "proton therapy",
      "radiation therapy",
      "pediatric radiation",
      "skull base tumor",
      "chordoma",
      "chondrosarcoma",
      "re-irradiation",
      "dosimetric advantage",
      "organs at risk",
      "sbrt",
      "stereotactic radiosurgery",
      "head neck cancer radiation",
      "cns tumor radiation",
    ],
  },
  {
    org: "ASCO/CAP",
    document_title: "ASCO/CAP HER2 Testing in Breast Cancer: Guideline Update",
    document_type: "Clinical Practice Guideline",
    year: 2023,
    canonical_url: "https://pubmed.ncbi.nlm.nih.gov/37284804/",
    tags: [
      "her2 testing",
      "breast cancer",
      "immunohistochemistry",
      "fish",
      "her2 low",
      "trastuzumab deruxtecan",
      "ihc 0",
      "ihc 1+",
      "ihc 2+",
      "her2 overexpression",
      "her2 amplification",
      "in situ hybridization",
      "her2 negative",
      "her2 positive",
      "antibody drug conjugate",
    ],
  },
  {
    org: "ASCO",
    document_title:
      "ASCO Guideline Update: Systemic Therapy for Advanced HER2-Positive Breast Cancer",
    document_type: "Clinical Practice Guideline",
    year: 2022,
    canonical_url: "https://pubmed.ncbi.nlm.nih.gov/35640077/",
    tags: [
      "her2 positive breast cancer",
      "trastuzumab",
      "pertuzumab",
      "trastuzumab deruxtecan",
      "t-dxd",
      "trastuzumab emtansine",
      "t-dm1",
      "tucatinib",
      "neratinib",
      "lapatinib",
      "brain metastases breast cancer",
      "second line her2",
      "margetuximab",
      "metastatic breast cancer",
    ],
  },
  {
    org: "ASCO",
    document_title:
      "ASCO Guideline Update: Endocrine Treatment and Targeted Therapy for HR-Positive, HER2-Negative Metastatic Breast Cancer",
    document_type: "Clinical Practice Guideline",
    year: 2021,
    canonical_url: "https://pubmed.ncbi.nlm.nih.gov/34324367/",
    tags: [
      "hormone receptor positive",
      "hr positive her2 negative",
      "metastatic breast cancer",
      "cdk4 6 inhibitor",
      "palbociclib",
      "ribociclib",
      "abemaciclib",
      "fulvestrant",
      "aromatase inhibitor",
      "esr1 mutation",
      "pik3ca mutation",
      "alpelisib",
      "capivasertib",
      "endocrine therapy",
      "endocrine resistance",
    ],
  },
  {
    org: "ASCO",
    document_title: "ASCO Guideline: Treatment of Metastatic Colorectal Cancer",
    document_type: "Clinical Practice Guideline",
    year: 2023,
    canonical_url: "https://pubmed.ncbi.nlm.nih.gov/36252154/",
    tags: [
      "metastatic colorectal cancer",
      "mcrc",
      "folfox",
      "folfiri",
      "folfoxiri",
      "bevacizumab",
      "cetuximab",
      "panitumumab",
      "kras",
      "ras wildtype",
      "braf v600e",
      "msi high",
      "dmmr",
      "pembrolizumab colorectal",
      "liver metastases",
      "sbrt liver",
      "hipec",
      "peritoneal metastases",
    ],
  },
  {
    org: "ASCO",
    document_title:
      "ASCO Guideline: Management of Locally Advanced Rectal Cancer",
    document_type: "Clinical Practice Guideline",
    year: 2024,
    canonical_url: "https://pubmed.ncbi.nlm.nih.gov/39116386/",
    tags: [
      "rectal cancer",
      "locally advanced rectal cancer",
      "total neoadjuvant therapy",
      "tnt",
      "chemoradiation",
      "neoadjuvant chemotherapy rectal",
      "watch and wait",
      "complete clinical response",
      "mesorectal excision",
      "tme",
      "radiation rectal cancer",
      "immunotherapy rectal cancer",
    ],
  },
  {
    org: "ASCO",
    document_title:
      "ASCO Guideline Update: Initial Management of Noncastrate Advanced, Recurrent, or Metastatic Prostate Cancer",
    document_type: "Clinical Practice Guideline",
    year: 2023,
    canonical_url: "https://ascopubs.org/doi/10.1200/JCO.23.00155",
    tags: [
      "metastatic prostate cancer",
      "noncastrate",
      "castration sensitive",
      "androgen deprivation therapy",
      "adt",
      "enzalutamide",
      "abiraterone",
      "apalutamide",
      "darolutamide",
      "docetaxel prostate",
      "triplet therapy prostate",
      "high volume disease",
      "low volume disease",
      "arat",
      "androgen receptor axis targeted",
    ],
  },
  {
    org: "ASCO",
    document_title:
      "ASCO Guideline: Systemic Therapy in Patients With Metastatic Castration-Resistant Prostate Cancer",
    document_type: "Clinical Practice Guideline",
    year: 2025,
    canonical_url: "https://pubmed.ncbi.nlm.nih.gov/40315400/",
    tags: [
      "mcrpc",
      "castration resistant prostate cancer",
      "lu psma 617",
      "lutetium psma",
      "olaparib prostate",
      "niraparib",
      "talazoparib",
      "parp inhibitor prostate",
      "brca prostate",
      "hrr mutation",
      "homologous recombination repair",
      "cabazitaxel",
      "radium 223",
      "sipuleucel-t",
      "psma pet",
    ],
  },
  {
    org: "ASCO",
    document_title: "ASCO Guideline Update: Antiemetics",
    document_type: "Clinical Practice Guideline",
    year: 2020,
    canonical_url: "https://pubmed.ncbi.nlm.nih.gov/32658626/",
    tags: [
      "antiemetics",
      "chemotherapy induced nausea vomiting",
      "cinv",
      "olanzapine",
      "aprepitant",
      "fosaprepitant",
      "ondansetron",
      "granisetron",
      "palonosetron",
      "dexamethasone antiemetic",
      "nk1 receptor antagonist",
      "5ht3 antagonist",
      "high emetogenic risk",
      "moderate emetogenic risk",
      "breakthrough nausea",
    ],
  },
  {
    org: "ASCO",
    document_title:
      "ASCO Guideline Update: Integration of Palliative Care Into Standard Oncology Care",
    document_type: "Clinical Practice Guideline",
    year: 2017,
    canonical_url: "https://pubmed.ncbi.nlm.nih.gov/27768323/",
    tags: [
      "palliative care",
      "supportive care oncology",
      "goals of care",
      "advance care planning",
      "symptom management cancer",
      "quality of life cancer",
      "early palliative care",
      "concurrent palliative care",
      "hospice referral",
      "pain management cancer",
    ],
  },
  {
    org: "ASCO",
    document_title: "ASCO Guideline: Metastatic Pancreatic Cancer",
    document_type: "Clinical Practice Guideline",
    year: 2020,
    canonical_url: "https://pubmed.ncbi.nlm.nih.gov/32986498/",
    tags: [
      "pancreatic cancer",
      "metastatic pancreatic cancer",
      "folfirinox",
      "gemcitabine nab-paclitaxel",
      "pancreatic adenocarcinoma",
      "second line pancreatic",
      "nanoliposomal irinotecan",
      "brca pancreatic",
      "olaparib pancreatic",
      "microsatellite instability pancreatic",
      "biliary stent",
      "exocrine insufficiency",
    ],
  },
  {
    org: "ASTRO",
    document_title:
      "ASTRO Guideline: Stereotactic Body Radiation Therapy for Early-Stage Non-Small Cell Lung Cancer",
    document_type: "Clinical Practice Guideline",
    year: 2021,
    canonical_url: "https://pubmed.ncbi.nlm.nih.gov/34118399/",
    tags: [
      "sbrt lung",
      "stereotactic body radiation therapy",
      "early stage nsclc",
      "medically inoperable lung cancer",
      "stage i nsclc",
      "peripheral lung tumor",
      "central lung tumor",
      "ultra central lung",
      "sbrt dosing",
      "sbrt fractionation",
      "lung stereotactic ablative radiotherapy",
      "sabr",
    ],
  },
  {
    org: "ASTRO/SNO",
    document_title: "ASTRO/SNO Guideline: Treatment of Brain Metastases",
    document_type: "Clinical Practice Guideline",
    year: 2022,
    canonical_url: "https://pubmed.ncbi.nlm.nih.gov/35016994/",
    tags: [
      "brain metastases",
      "stereotactic radiosurgery",
      "srs brain",
      "whole brain radiation",
      "wbrt",
      "hippocampal avoidance",
      "memantine",
      "gamma knife",
      "cyberknife",
      "brain metastases resection",
      "postoperative srs",
      "leptomeningeal disease",
      "brain metastases immunotherapy",
    ],
  },

  // --- RHEUMATOLOGY ---
  {
    org: "ACR",
    document_title:
      "2021 ACR Guideline for the Treatment of Rheumatoid Arthritis",
    document_type: "Clinical Practice Guideline",
    year: 2021,
    canonical_url: "https://pmc.ncbi.nlm.nih.gov/articles/PMC9273041/",
    tags: [
      "rheumatoid arthritis",
      "ra",
      "methotrexate",
      "biologic dmard",
      "tnf inhibitor",
      "jak inhibitor",
      "dmard",
      "disease modifying antirheumatic drug",
      "treat to target",
    ],
  },
  {
    org: "ACR",
    document_title: "2020 ACR Guideline for the Management of Gout",
    document_type: "Clinical Practice Guideline",
    year: 2020,
    canonical_url: "https://pmc.ncbi.nlm.nih.gov/articles/PMC10563586/",
    tags: [
      "gout",
      "urate lowering therapy",
      "allopurinol",
      "febuxostat",
      "pegloticase",
      "colchicine",
      "hyperuricemia",
      "tophi",
      "gout flare",
    ],
  },
  {
    org: "ACR/NPF",
    document_title:
      "2018 ACR/NPF Guideline for the Treatment of Psoriatic Arthritis",
    document_type: "Clinical Practice Guideline",
    year: 2018,
    canonical_url: "https://pmc.ncbi.nlm.nih.gov/articles/PMC8265826/",
    tags: [
      "psoriatic arthritis",
      "psa",
      "tnf inhibitor",
      "il-17 inhibitor",
      "il-12/23 inhibitor",
      "apremilast",
      "dactylitis",
      "enthesitis",
      "psoriasis",
    ],
  },
  {
    org: "ACR/SAA/SPARTAN",
    document_title:
      "2019 ACR/SAA/SPARTAN Recommendations for Treatment of Ankylosing Spondylitis and Nonradiographic Axial Spondyloarthritis",
    document_type: "Clinical Practice Guideline",
    year: 2019,
    canonical_url: "https://pmc.ncbi.nlm.nih.gov/articles/PMC6764857/",
    tags: [
      "ankylosing spondylitis",
      "axial spondyloarthritis",
      "nr-axspa",
      "hla-b27",
      "sacroiliitis",
      "tnf inhibitor",
      "secukinumab",
      "ixekizumab",
      "biologic tapering",
    ],
  },
  {
    org: "ACR",
    document_title:
      "2024 ACR Guideline for the Screening, Treatment, and Management of Lupus Nephritis",
    document_type: "Clinical Practice Guideline",
    year: 2024,
    canonical_url: "https://pubmed.ncbi.nlm.nih.gov/40331662/",
    tags: [
      "lupus nephritis",
      "systemic lupus erythematosus",
      "sle",
      "mycophenolate",
      "belimumab",
      "voclosporin",
      "calcineurin inhibitor",
      "class iii",
      "class iv",
      "class v",
      "proteinuria",
    ],
  },
  {
    org: "ACR",
    document_title:
      "2025 ACR Guideline for the Treatment of Systemic Lupus Erythematosus",
    document_type: "Clinical Practice Guideline",
    year: 2025,
    canonical_url: "https://pubmed.ncbi.nlm.nih.gov/41182321/",
    tags: [
      "systemic lupus erythematosus",
      "sle",
      "lupus",
      "hydroxychloroquine",
      "immunosuppressive therapy",
      "biologic therapy",
      "anifrolumab",
      "belimumab",
    ],
  },
  {
    org: "ACR/VF",
    document_title:
      "2021 ACR/Vasculitis Foundation Guideline for Management of ANCA-Associated Vasculitis",
    document_type: "Clinical Practice Guideline",
    year: 2021,
    canonical_url: "https://pubmed.ncbi.nlm.nih.gov/34235880/",
    tags: [
      "anca vasculitis",
      "granulomatosis with polyangiitis",
      "gpa",
      "microscopic polyangiitis",
      "mpa",
      "eosinophilic granulomatosis",
      "egpa",
      "rituximab",
      "cyclophosphamide",
      "remission induction",
    ],
  },
  {
    org: "ACR/VF",
    document_title:
      "2021 ACR/Vasculitis Foundation Guideline for Management of Giant Cell Arteritis and Takayasu Arteritis",
    document_type: "Clinical Practice Guideline",
    year: 2021,
    canonical_url: "https://pubmed.ncbi.nlm.nih.gov/34235884/",
    tags: [
      "giant cell arteritis",
      "gca",
      "takayasu arteritis",
      "temporal arteritis",
      "large vessel vasculitis",
      "tocilizumab",
      "temporal artery biopsy",
      "polymyalgia rheumatica",
    ],
  },
  {
    org: "ACR",
    document_title:
      "2022 ACR Guideline for Prevention and Treatment of Glucocorticoid-Induced Osteoporosis",
    document_type: "Clinical Practice Guideline",
    year: 2022,
    canonical_url: "https://pubmed.ncbi.nlm.nih.gov/37845798/",
    tags: [
      "glucocorticoid induced osteoporosis",
      "giop",
      "bisphosphonate",
      "denosumab",
      "teriparatide",
      "fracture risk",
      "bone mineral density",
      "frax",
      "steroid osteoporosis",
    ],
  },
  {
    org: "ACR/AF",
    document_title:
      "2019 ACR/Arthritis Foundation Guideline for Management of Osteoarthritis of the Hand, Hip, and Knee",
    document_type: "Clinical Practice Guideline",
    year: 2019,
    canonical_url: "https://pubmed.ncbi.nlm.nih.gov/31908149/",
    tags: [
      "osteoarthritis",
      "knee osteoarthritis",
      "hip osteoarthritis",
      "hand osteoarthritis",
      "intraarticular corticosteroid injection",
      "viscosupplementation",
      "hyaluronic acid injection",
      "topical nsaid",
      "oral nsaid",
      "duloxetine",
      "acetaminophen",
      "platelet rich plasma",
      "radiofrequency ablation knee",
    ],
  },
  {
    org: "Endocrine Society",
    document_title:
      "Endocrine Society Guideline for Pharmacological Management of Osteoporosis in Postmenopausal Women (2019 + 2020 Romosozumab Update)",
    document_type: "Clinical Practice Guideline",
    year: 2020,
    canonical_url: "https://pubmed.ncbi.nlm.nih.gov/30907953/",
    tags: [
      "osteoporosis",
      "postmenopausal osteoporosis",
      "bisphosphonate",
      "denosumab",
      "romosozumab",
      "teriparatide",
      "abaloparatide",
      "bisphosphonate holiday",
      "bone mineral density",
      "dexa scan",
      "fracture prevention",
      "sequential osteoporosis therapy",
      "zoledronic acid",
      "alendronate",
    ],
  },
  {
    org: "ACR/AF",
    document_title:
      "2019 ACR/Arthritis Foundation Guideline for Treatment of JIA: Non-Systemic Polyarthritis, Sacroiliitis, and Enthesitis",
    document_type: "Clinical Practice Guideline",
    year: 2019,
    canonical_url: "https://pubmed.ncbi.nlm.nih.gov/31021516/",
    tags: [
      "juvenile idiopathic arthritis",
      "jia",
      "polyarticular jia",
      "sacroiliitis juvenile",
      "enthesitis",
      "biologic dmard jia",
      "tnf inhibitor pediatric",
      "methotrexate jia",
      "pediatric rheumatology",
      "treat to target jia",
      "enthesitis related arthritis",
    ],
  },
  {
    org: "ACR",
    document_title:
      "2021 ACR Guideline for Treatment of JIA: Oligoarthritis, TMJ Arthritis, and Systemic JIA",
    document_type: "Clinical Practice Guideline",
    year: 2022,
    canonical_url: "https://pubmed.ncbi.nlm.nih.gov/35233986/",
    tags: [
      "juvenile idiopathic arthritis",
      "systemic jia",
      "oligoarthritis",
      "macrophage activation syndrome",
      "mas",
      "il-1 inhibitor",
      "il-6 inhibitor",
      "anakinra",
      "canakinumab",
      "tocilizumab jia",
      "tmj arthritis",
      "biologic dmard pediatric",
    ],
  },
  {
    org: "ACR/CHEST",
    document_title:
      "2023 ACR/CHEST Guideline for Treatment of Interstitial Lung Disease in Systemic Autoimmune Rheumatic Diseases",
    document_type: "Clinical Practice Guideline",
    year: 2024,
    canonical_url: "https://pubmed.ncbi.nlm.nih.gov/38973731/",
    tags: [
      "interstitial lung disease",
      "scleroderma ild",
      "systemic sclerosis ild",
      "myositis ild",
      "dermatomyositis ild",
      "rheumatoid arthritis ild",
      "mycophenolate ild",
      "rituximab ild",
      "nintedanib",
      "tocilizumab scleroderma",
      "cyclophosphamide ild",
      "rapidly progressive ild",
      "sard-ild",
    ],
  },
  {
    org: "ACR/CHEST",
    document_title:
      "2023 ACR/CHEST Guideline for Screening and Monitoring of Interstitial Lung Disease in Systemic Autoimmune Rheumatic Diseases",
    document_type: "Clinical Practice Guideline",
    year: 2024,
    canonical_url: "https://pubmed.ncbi.nlm.nih.gov/38973714/",
    tags: [
      "interstitial lung disease screening",
      "hrct chest",
      "pulmonary function test ild",
      "systemic sclerosis screening",
      "myositis ild screening",
      "sjogren ild",
      "rheumatoid arthritis ild screening",
      "sard-ild monitoring",
      "fvc decline",
    ],
  },
  {
    org: "ACR/AAHKS",
    document_title:
      "2022 ACR/AAHKS Guideline for Perioperative Management of Antirheumatic Medication in Patients Undergoing Elective Total Hip or Total Knee Arthroplasty",
    document_type: "Clinical Practice Guideline",
    year: 2022,
    canonical_url: "https://pubmed.ncbi.nlm.nih.gov/35718887/",
    tags: [
      "perioperative management",
      "biologic hold surgery",
      "dmard perioperative",
      "total hip arthroplasty",
      "total knee arthroplasty",
      "joint replacement rheumatoid",
      "withhold biologic surgery",
      "restart biologic postoperative",
      "infection risk arthroplasty",
      "stress dose steroids",
      "glucocorticoid perioperative",
      "tofacitinib perioperative",
      "rituximab perioperative",
      "periprosthetic joint infection",
    ],
  },
  {
    org: "ACR",
    document_title:
      "2022 ACR Guideline for Vaccinations in Patients With Rheumatic and Musculoskeletal Diseases",
    document_type: "Clinical Practice Guideline",
    year: 2023,
    canonical_url: "https://pubmed.ncbi.nlm.nih.gov/36597813/",
    tags: [
      "vaccination immunosuppressed",
      "vaccine rheumatic disease",
      "live vaccine immunosuppression",
      "methotrexate vaccine hold",
      "rituximab vaccine timing",
      "shingrix immunosuppressed",
      "pneumococcal vaccine rheumatology",
      "influenza vaccine biologic",
      "hpv vaccine autoimmune",
      "immunocompromised vaccination schedule",
    ],
  },
  {
    org: "ACR",
    document_title:
      "2020 ACR Guideline for the Management of Reproductive Health in Rheumatic and Musculoskeletal Diseases",
    document_type: "Clinical Practice Guideline",
    year: 2020,
    canonical_url: "https://pubmed.ncbi.nlm.nih.gov/32090466/",
    tags: [
      "pregnancy rheumatic disease",
      "biologic pregnancy safety",
      "methotrexate teratogen",
      "mycophenolate pregnancy",
      "fertility preservation cyclophosphamide",
      "contraception lupus",
      "antiphospholipid pregnancy",
      "anti-ro ssa pregnancy",
      "hydroxychloroquine pregnancy",
      "lactation rheumatic medication",
      "reproductive health rheumatology",
    ],
  },
  {
    org: "EULAR",
    document_title:
      "EULAR Recommendations for the Treatment of Systemic Sclerosis: 2023 Update",
    document_type: "Clinical Practice Guideline",
    year: 2025,
    canonical_url: "https://pubmed.ncbi.nlm.nih.gov/39874231/",
    tags: [
      "systemic sclerosis",
      "scleroderma",
      "raynaud phenomenon",
      "digital ulcer",
      "scleroderma renal crisis",
      "pulmonary arterial hypertension ssc",
      "skin fibrosis",
      "tocilizumab scleroderma",
      "nintedanib ssc",
      "rituximab scleroderma",
      "mycophenolate ssc",
      "autologous stem cell transplant ssc",
      "calcium channel blocker raynaud",
      "bosentan digital ulcer",
    ],
  },
  {
    org: "EULAR/ACR",
    document_title:
      "2017 EULAR/ACR Classification Criteria for Adult and Juvenile Idiopathic Inflammatory Myopathies and Their Major Subgroups",
    document_type: "Classification Criteria",
    year: 2017,
    canonical_url: "https://pubmed.ncbi.nlm.nih.gov/29079590/",
    tags: [
      "inflammatory myopathy",
      "dermatomyositis",
      "polymyositis",
      "inclusion body myositis",
      "immune mediated necrotizing myopathy",
      "anti-jo1 antibody",
      "myositis specific antibody",
      "anti-mda5",
      "antisynthetase syndrome",
      "ivig dermatomyositis",
      "muscle biopsy myositis",
      "creatine kinase elevated",
      "juvenile dermatomyositis",
    ],
  },
  {
    org: "ACR/EULAR",
    document_title:
      "2023 ACR/EULAR Antiphospholipid Syndrome Classification Criteria",
    document_type: "Classification Criteria",
    year: 2023,
    canonical_url: "https://pubmed.ncbi.nlm.nih.gov/37635643/",
    tags: [
      "antiphospholipid syndrome",
      "aps",
      "lupus anticoagulant",
      "anticardiolipin antibody",
      "anti-beta2 glycoprotein",
      "thrombosis autoimmune",
      "recurrent pregnancy loss",
      "catastrophic aps",
      "anticoagulation aps",
      "warfarin aps",
      "thrombotic microangiopathy",
    ],
  },
  {
    org: "ACR",
    document_title:
      "2016 Revisions to the 2010/2011 ACR Fibromyalgia Diagnostic Criteria",
    document_type: "Diagnostic Criteria",
    year: 2016,
    canonical_url: "https://pubmed.ncbi.nlm.nih.gov/27916278/",
    tags: [
      "fibromyalgia",
      "widespread pain index",
      "symptom severity scale",
      "central sensitization",
      "fibromyalgia criteria",
      "chronic widespread pain",
      "pregabalin fibromyalgia",
      "duloxetine fibromyalgia",
      "milnacipran",
      "polysymptomatic distress",
    ],
  },

  // --- PSYCHIATRY ---
  {
    org: "APA",
    document_title:
      "American Psychiatric Association Practice Guideline for the Treatment of Patients With Schizophrenia, 3rd Edition",
    document_type: "Clinical Practice Guideline",
    year: 2020,
    canonical_url: "https://pubmed.ncbi.nlm.nih.gov/32867516/",
    tags: [
      "schizophrenia",
      "antipsychotic",
      "clozapine",
      "treatment resistant schizophrenia",
      "long acting injectable antipsychotic",
      "lai antipsychotic",
      "first episode psychosis",
      "psychosis",
      "cognitive behavioral therapy psychosis",
      "tardive dyskinesia",
      "akathisia",
      "metabolic monitoring antipsychotic",
      "atypical antipsychotic",
      "depot injection",
    ],
  },
  {
    org: "APA",
    document_title:
      "APA Clinical Practice Guideline for the Treatment of Depression Across Three Age Cohorts",
    document_type: "Clinical Practice Guideline",
    year: 2019,
    canonical_url: "https://pubmed.ncbi.nlm.nih.gov/34843274/",
    tags: [
      "major depressive disorder",
      "depression",
      "mdd",
      "antidepressant",
      "ssri",
      "snri",
      "psychotherapy",
      "cbt depression",
      "treatment resistant depression",
      "persistent depressive disorder",
      "dysthymia",
      "pharmacotherapy depression",
      "combination therapy depression",
    ],
  },
  {
    org: "APA",
    document_title:
      "APA Practice Guideline for the Treatment of Bipolar Disorder",
    document_type: "Clinical Practice Guideline",
    year: 2023,
    canonical_url: "https://pubmed.ncbi.nlm.nih.gov/37490355/",
    tags: [
      "bipolar disorder",
      "bipolar i",
      "bipolar ii",
      "lithium",
      "mood stabilizer",
      "valproate",
      "lamotrigine",
      "quetiapine",
      "manic episode",
      "bipolar depression",
      "maintenance therapy bipolar",
    ],
  },
  {
    org: "APA",
    document_title:
      "APA Practice Guideline for the Treatment of Posttraumatic Stress Disorder (PTSD) in Adults",
    document_type: "Clinical Practice Guideline",
    year: 2017,
    canonical_url: "https://pubmed.ncbi.nlm.nih.gov/28158912/",
    tags: [
      "ptsd",
      "posttraumatic stress disorder",
      "trauma",
      "cpe therapy",
      "emdr",
      "prolonged exposure therapy",
      "cognitive processing therapy",
      "sertraline ptsd",
      "prazosin nightmares",
    ],
  },
  {
    org: "APA",
    document_title:
      "APA Practice Guideline for the Treatment of Substance Use Disorders",
    document_type: "Clinical Practice Guideline",
    year: 2023,
    canonical_url: "https://pubmed.ncbi.nlm.nih.gov/36999725/",
    tags: [
      "substance use disorder",
      "opioid use disorder",
      "alcohol use disorder",
      "buprenorphine",
      "methadone",
      "naltrexone",
      "medication assisted treatment",
      "mat",
      "moud",
      "addiction treatment",
      "suboxone",
    ],
  },

  // --- ENT / OTOLARYNGOLOGY ---
  {
    org: "AAO-HNS",
    document_title:
      "AAO-HNSF Clinical Practice Guideline (Update): Adult Sinusitis",
    document_type: "Clinical Practice Guideline",
    year: 2015,
    canonical_url: "https://pubmed.ncbi.nlm.nih.gov/25832968/",
    tags: [
      "sinusitis",
      "rhinosinusitis",
      "chronic rhinosinusitis",
      "acute bacterial rhinosinusitis",
      "endoscopic sinus surgery",
      "fess",
      "nasal polyps",
      "nasal saline irrigation",
      "watchful waiting sinusitis",
      "ct sinuses",
      "amoxicillin clavulanate",
      "recurrent sinusitis",
      "nasal endoscopy",
      "maxillary sinusitis",
      "frontal sinusitis",
    ],
  },
  {
    org: "AAO-HNS",
    document_title:
      "AAO-HNSF Clinical Practice Guideline: Tonsillectomy in Children (Update)",
    document_type: "Clinical Practice Guideline",
    year: 2019,
    canonical_url: "https://pubmed.ncbi.nlm.nih.gov/30798778/",
    tags: [
      "tonsillectomy",
      "adenoidectomy",
      "tonsillectomy and adenoidectomy",
      "recurrent throat infection",
      "obstructive sleep apnea children",
      "tonsillar hypertrophy",
      "sleep disordered breathing",
      "polysomnography",
      "paradise criteria",
      "peritonsillar abscess",
      "pfapa",
      "recurrent streptococcal pharyngitis",
      "pediatric osa",
    ],
  },
  {
    org: "AAO-HNS",
    document_title:
      "AAO-HNSF Clinical Practice Guideline: Tympanostomy Tubes in Children (Update)",
    document_type: "Clinical Practice Guideline",
    year: 2022,
    canonical_url: "https://pubmed.ncbi.nlm.nih.gov/35138954/",
    tags: [
      "tympanostomy tubes",
      "ear tubes",
      "myringotomy",
      "otitis media with effusion",
      "recurrent acute otitis media",
      "hearing loss child",
      "ventilation tubes",
      "pressure equalization tubes",
    ],
  },
  {
    org: "AAO-HNS",
    document_title:
      "AAO-HNSF Clinical Practice Guideline: Sudden Hearing Loss (Update)",
    document_type: "Clinical Practice Guideline",
    year: 2019,
    canonical_url: "https://pubmed.ncbi.nlm.nih.gov/31369359/",
    tags: [
      "sudden sensorineural hearing loss",
      "sudden hearing loss",
      "sshl",
      "intratympanic steroid",
      "oral corticosteroid hearing",
      "audiometry",
      "hearing loss workup",
      "mri iac",
    ],
  },
  {
    org: "AAO-HNS",
    document_title:
      "AAO-HNSF Clinical Consensus Statement: Septoplasty with or without Inferior Turbinate Reduction",
    document_type: "Clinical Consensus Statement",
    year: 2015,
    canonical_url: "https://pubmed.ncbi.nlm.nih.gov/26527752/",
    tags: [
      "septoplasty",
      "deviated septum",
      "nasal obstruction",
      "turbinate reduction",
      "inferior turbinate hypertrophy",
      "nasal airway obstruction",
      "septorhinoplasty",
      "nasal valve",
      "nose scale",
      "functional nasal surgery",
      "turbinoplasty",
      "nasal breathing",
      "ct scan septum",
    ],
  },
  {
    org: "AAO-HNS",
    document_title: "AAO-HNSF Clinical Practice Guideline: Allergic Rhinitis",
    document_type: "Clinical Practice Guideline",
    year: 2015,
    canonical_url: "https://pubmed.ncbi.nlm.nih.gov/25644617/",
    tags: [
      "allergic rhinitis",
      "hay fever",
      "nasal allergy",
      "intranasal corticosteroid",
      "oral antihistamine",
      "immunotherapy",
      "subcutaneous immunotherapy",
      "sublingual immunotherapy",
      "slit",
      "scit",
      "allergy testing",
      "ige testing",
      "skin prick test",
      "nasal congestion",
      "seasonal allergy",
      "perennial allergy",
    ],
  },
  {
    org: "AAO-HNS",
    document_title:
      "AAO-HNSF Clinical Practice Guideline: Benign Paroxysmal Positional Vertigo (Update)",
    document_type: "Clinical Practice Guideline",
    year: 2017,
    canonical_url: "https://pubmed.ncbi.nlm.nih.gov/28248609/",
    tags: [
      "bppv",
      "benign paroxysmal positional vertigo",
      "vertigo",
      "dizziness",
      "dix-hallpike",
      "epley maneuver",
      "canalith repositioning",
      "vestibular",
      "positional vertigo",
      "nystagmus",
      "vestibular rehabilitation",
      "semicircular canal",
    ],
  },
  {
    org: "AAO-HNS",
    document_title: "AAO-HNSF Clinical Practice Guideline: Tinnitus",
    document_type: "Clinical Practice Guideline",
    year: 2014,
    canonical_url: "https://pubmed.ncbi.nlm.nih.gov/25273878/",
    tags: [
      "tinnitus",
      "ringing in ears",
      "bothersome tinnitus",
      "persistent tinnitus",
      "tinnitus hearing aid",
      "cognitive behavioral therapy tinnitus",
      "sound therapy",
      "pulsatile tinnitus",
      "tinnitus evaluation",
      "hearing loss tinnitus",
    ],
  },
  {
    org: "AAO-HNS",
    document_title:
      "AAO-HNSF Clinical Practice Guideline: Age-Related Hearing Loss",
    document_type: "Clinical Practice Guideline",
    year: 2024,
    canonical_url: "https://pubmed.ncbi.nlm.nih.gov/38687845/",
    tags: [
      "age-related hearing loss",
      "presbycusis",
      "hearing aid",
      "hearing amplification",
      "cochlear implant referral",
      "hearing screening",
      "audiogram",
      "hearing loss older adults",
      "otc hearing aid",
      "hearing rehabilitation",
      "arhl",
    ],
  },

  // --- DERMATOLOGY ---
  {
    org: "AAD/NPF",
    document_title:
      "Joint AAD-NPF Guidelines of Care for the Management and Treatment of Psoriasis With Biologics",
    document_type: "Clinical Practice Guideline",
    year: 2019,
    canonical_url: "https://pubmed.ncbi.nlm.nih.gov/30772098/",
    tags: [
      "psoriasis",
      "plaque psoriasis",
      "biologic therapy psoriasis",
      "adalimumab",
      "etanercept",
      "ustekinumab",
      "secukinumab",
      "ixekizumab",
      "guselkumab",
      "infliximab",
      "moderate to severe psoriasis",
      "pasi",
      "tnf inhibitor psoriasis",
      "il-17 inhibitor",
      "il-23 inhibitor",
      "brodalumab",
      "risankizumab",
    ],
  },
  {
    org: "AAD",
    document_title:
      "AAD Guidelines of Care for the Management of Atopic Dermatitis in Adults With Phototherapy and Systemic Therapies",
    document_type: "Clinical Practice Guideline",
    year: 2024,
    canonical_url: "https://pubmed.ncbi.nlm.nih.gov/37943240/",
    tags: [
      "atopic dermatitis",
      "eczema",
      "dupilumab",
      "tralokinumab",
      "upadacitinib",
      "abrocitinib",
      "baricitinib",
      "jak inhibitor eczema",
      "biologic eczema",
      "moderate to severe eczema",
      "phototherapy eczema",
      "cyclosporine eczema",
      "methotrexate eczema",
      "systemic therapy atopic dermatitis",
    ],
  },
  {
    org: "AAD",
    document_title:
      "AAD Guidelines of Care for the Management of Acne Vulgaris",
    document_type: "Clinical Practice Guideline",
    year: 2024,
    canonical_url: "https://pubmed.ncbi.nlm.nih.gov/37852524/",
    tags: [
      "acne vulgaris",
      "isotretinoin",
      "accutane",
      "acne treatment",
      "topical retinoid",
      "benzoyl peroxide",
      "spironolactone acne",
      "oral antibiotic acne",
      "hormonal acne",
      "nodular acne",
    ],
  },
  {
    org: "AAD",
    document_title:
      "AAD Guidelines of Care for the Management and Treatment of Psoriasis With Topical Therapy and Alternative Medicine",
    document_type: "Clinical Practice Guideline",
    year: 2021,
    canonical_url: "https://pubmed.ncbi.nlm.nih.gov/33321207/",
    tags: [
      "psoriasis topical",
      "topical corticosteroid psoriasis",
      "calcipotriene",
      "tazarotene",
      "roflumilast topical",
      "tapinarof",
      "mild psoriasis",
      "scalp psoriasis",
      "psoriasis phototherapy",
      "nbuvb",
    ],
  },
  {
    org: "AAD/NPF",
    document_title:
      "Joint AAD-NPF Guidelines of Care for the Management of Psoriasis With Systemic Nonbiologic Therapies",
    document_type: "Clinical Practice Guideline",
    year: 2020,
    canonical_url: "https://pubmed.ncbi.nlm.nih.gov/32119894/",
    tags: [
      "psoriasis systemic",
      "methotrexate psoriasis",
      "cyclosporine psoriasis",
      "acitretin",
      "apremilast",
      "tofacitinib psoriasis",
      "oral systemic psoriasis",
      "moderate to severe psoriasis",
      "psoriatic arthritis methotrexate",
      "fumaric acid esters",
      "nonbiologic systemic",
      "pde4 inhibitor psoriasis",
      "hepatotoxicity monitoring",
      "step therapy psoriasis",
    ],
  },
  {
    org: "US/Canadian HS Foundations",
    document_title:
      "North American Clinical Management Guidelines for Hidradenitis Suppurativa: Part II Medical Management",
    document_type: "Clinical Practice Guideline",
    year: 2019,
    canonical_url: "https://pubmed.ncbi.nlm.nih.gov/30872149/",
    tags: [
      "hidradenitis suppurativa",
      "acne inversa",
      "adalimumab hidradenitis",
      "infliximab hidradenitis",
      "secukinumab hidradenitis",
      "clindamycin rifampin",
      "biologic hidradenitis",
      "hurley stage",
      "hs treatment",
      "hormonal therapy hidradenitis",
      "spironolactone hidradenitis",
      "finasteride hidradenitis",
      "hs surgery",
      "laser hidradenitis",
      "deroofing",
      "intralesional triamcinolone hidradenitis",
    ],
  },
  {
    org: "AAD",
    document_title:
      "AAD Guidelines of Care for the Management of Actinic Keratosis",
    document_type: "Clinical Practice Guideline",
    year: 2021,
    canonical_url: "https://pubmed.ncbi.nlm.nih.gov/33820677/",
    tags: [
      "actinic keratosis",
      "actinic keratosis treatment",
      "cryosurgery",
      "photodynamic therapy",
      "pdt skin",
      "fluorouracil topical",
      "5-fu topical",
      "imiquimod",
      "ingenol mebutate",
      "tirbanibulin",
      "diclofenac topical",
      "field therapy skin",
      "keratinocyte carcinoma prevention",
      "precancerous skin lesion",
      "aminolevulinic acid",
    ],
  },
  {
    org: "BAD",
    document_title:
      "British Association of Dermatologists Living Guideline for Managing People With Alopecia Areata",
    document_type: "Clinical Practice Guideline",
    year: 2024,
    canonical_url: "https://pubmed.ncbi.nlm.nih.gov/39432739/",
    tags: [
      "alopecia areata",
      "hair loss",
      "alopecia totalis",
      "alopecia universalis",
      "baricitinib alopecia",
      "ritlecitinib",
      "deuruxolitinib",
      "jak inhibitor alopecia",
      "topical corticosteroid alopecia",
      "intralesional triamcinolone alopecia",
      "contact immunotherapy alopecia",
      "salt score",
      "autoimmune hair loss",
      "severe alopecia areata",
    ],
  },
  {
    org: "EAACI/GA²LEN/EuroGuiDerm/APAAACI",
    document_title:
      "International Guideline for the Definition, Classification, Diagnosis, and Management of Urticaria",
    document_type: "Clinical Practice Guideline",
    year: 2022,
    canonical_url: "https://pubmed.ncbi.nlm.nih.gov/34536239/",
    tags: [
      "chronic urticaria",
      "chronic spontaneous urticaria",
      "omalizumab urticaria",
      "chronic inducible urticaria",
      "antihistamine urticaria",
      "cyclosporine urticaria",
      "urticaria treatment algorithm",
      "omalizumab step therapy",
      "h1 antihistamine updosing",
      "angioedema chronic",
      "urticaria control test",
      "xolair chronic urticaria",
      "mast cell urticaria",
      "wheals hives",
    ],
  },

  // --- ALLERGY / IMMUNOLOGY ---
  {
    org: "AAAAI/ACAAI",
    document_title:
      "AAAAI/ACAAI Joint Task Force Atopic Dermatitis (Eczema) Guidelines: GRADE- and IOM-Based Recommendations",
    document_type: "Clinical Practice Guideline",
    year: 2023,
    canonical_url: "https://pubmed.ncbi.nlm.nih.gov/38108679/",
    tags: [
      "atopic dermatitis allergy",
      "eczema guidelines",
      "topical corticosteroid eczema",
      "calcineurin inhibitor eczema",
      "crisaborole",
      "pde4 inhibitor eczema",
      "wet wrap therapy",
      "dilute bleach bath",
      "elimination diet eczema",
      "allergen immunotherapy eczema",
      "dupilumab eczema",
      "ruxolitinib topical",
      "topical jak inhibitor eczema",
    ],
  },
  {
    org: "GINA",
    document_title:
      "GINA Difficult-to-Treat and Severe Asthma: Diagnosis and Management Guide",
    document_type: "Clinical Practice Guideline",
    year: 2024,
    canonical_url: "https://ginasthma.org/severe-asthma/",
    tags: [
      "severe asthma",
      "difficult to treat asthma",
      "biologic asthma",
      "omalizumab",
      "mepolizumab",
      "benralizumab",
      "dupilumab asthma",
      "tezepelumab",
      "anti-ige",
      "anti-il5",
      "anti-il4r",
      "eosinophilic asthma",
      "allergic asthma",
      "type 2 inflammation",
      "oral corticosteroid sparing",
      "step 5 asthma",
      "uncontrolled asthma",
    ],
  },
  {
    org: "AAAAI/ACAAI",
    document_title: "AAAAI/ACAAI/JCAAI Practice Parameter: Chronic Urticaria",
    document_type: "Clinical Practice Guideline",
    year: 2022,
    canonical_url: "https://pubmed.ncbi.nlm.nih.gov/35667749/",
    tags: [
      "chronic urticaria",
      "chronic spontaneous urticaria",
      "csu",
      "antihistamine",
      "omalizumab urticaria",
      "xolair urticaria",
      "angioedema",
      "second generation antihistamine",
      "urticaria refractory",
    ],
  },
  {
    org: "AAAAI/ACAAI",
    document_title:
      "AAAAI/ACAAI Allergen Immunotherapy Practice Parameter Update",
    document_type: "Practice Parameter",
    year: 2020,
    canonical_url: "https://pubmed.ncbi.nlm.nih.gov/32645297/",
    tags: [
      "allergen immunotherapy",
      "allergy shots",
      "subcutaneous immunotherapy",
      "sublingual immunotherapy",
      "slit",
      "venom immunotherapy",
      "allergic rhinitis immunotherapy",
      "aeroallergen desensitization",
    ],
  },
  {
    org: "AAAAI/ACAAI",
    document_title: "AAAAI/ACAAI Anaphylaxis: A 2023 Practice Parameter Update",
    document_type: "Practice Parameter",
    year: 2023,
    canonical_url: "https://pubmed.ncbi.nlm.nih.gov/38108678/",
    tags: [
      "anaphylaxis",
      "epinephrine",
      "epinephrine autoinjector",
      "epipen",
      "biphasic anaphylaxis",
      "anaphylaxis diagnosis",
      "serum tryptase",
      "mast cell disorder",
      "mastocytosis",
      "venom anaphylaxis",
      "perioperative anaphylaxis",
      "food anaphylaxis",
      "drug anaphylaxis",
      "idiopathic anaphylaxis",
    ],
  },
  {
    org: "AAAAI/ACAAI",
    document_title: "AAAAI/ACAAI Rhinitis 2020: A Practice Parameter Update",
    document_type: "Practice Parameter",
    year: 2020,
    canonical_url: "https://pubmed.ncbi.nlm.nih.gov/32707227/",
    tags: [
      "allergic rhinitis",
      "nonallergic rhinitis",
      "seasonal allergic rhinitis",
      "perennial allergic rhinitis",
      "intranasal corticosteroid",
      "antihistamine",
      "nasal polyps",
      "chronic rhinosinusitis",
      "azelastine",
      "montelukast",
      "rhinitis pharmacotherapy",
      "local allergic rhinitis",
    ],
  },
  {
    org: "AAAAI/ACAAI",
    document_title:
      "AAAAI/ACAAI Drug Allergy: A 2022 Practice Parameter Update",
    document_type: "Practice Parameter",
    year: 2022,
    canonical_url: "https://pubmed.ncbi.nlm.nih.gov/36122788/",
    tags: [
      "drug allergy",
      "penicillin allergy",
      "beta lactam allergy",
      "drug hypersensitivity",
      "drug desensitization",
      "antibiotic allergy",
      "nsaid hypersensitivity",
      "aspirin exacerbated respiratory disease",
      "aerd",
      "drug challenge",
      "penicillin skin testing",
      "cephalosporin cross reactivity",
      "sulfonamide allergy",
      "drug rash",
      "dress syndrome",
      "stevens johnson syndrome",
      "sjs ten",
    ],
  },
  {
    org: "WAO/EAACI",
    document_title:
      "WAO/EAACI Guideline for the Management of Hereditary Angioedema — 2021 Revision and Update",
    document_type: "Clinical Practice Guideline",
    year: 2022,
    canonical_url: "https://pubmed.ncbi.nlm.nih.gov/35006617/",
    tags: [
      "hereditary angioedema",
      "hae",
      "c1 inhibitor deficiency",
      "c1 esterase inhibitor",
      "icatibant",
      "lanadelumab",
      "berotralstat",
      "ecallantide",
      "hae prophylaxis",
      "hae acute treatment",
      "bradykinin mediated angioedema",
      "acquired angioedema",
      "c1 inh concentrate",
      "haegarda",
      "takhzyro",
      "orladeyo",
    ],
  },
  {
    org: "AGA/AAAAI/ACAAI",
    document_title:
      "AGA/JTF Clinical Guidelines for the Management of Eosinophilic Esophagitis",
    document_type: "Clinical Practice Guideline",
    year: 2020,
    canonical_url: "https://pubmed.ncbi.nlm.nih.gov/32359562/",
    tags: [
      "eosinophilic esophagitis",
      "eoe",
      "topical steroids eoe",
      "budesonide eoe",
      "fluticasone eoe",
      "proton pump inhibitor eoe",
      "elimination diet eoe",
      "esophageal dilation",
      "dupilumab eoe",
      "food impaction",
      "dysphagia eosinophilic",
      "swallowed steroid",
    ],
  },
  {
    org: "AAAAI/ACAAI",
    document_title: "AAAAI/ACAAI Food Allergy: A Practice Parameter Update",
    document_type: "Practice Parameter",
    year: 2014,
    canonical_url: "https://pubmed.ncbi.nlm.nih.gov/25174862/",
    tags: [
      "food allergy",
      "peanut allergy",
      "tree nut allergy",
      "milk allergy",
      "egg allergy",
      "oral food challenge",
      "food specific ige",
      "skin prick testing food",
      "oral immunotherapy",
      "food allergy testing",
      "component resolved diagnostics",
      "omalizumab food allergy",
      "palforzia",
      "food allergy management",
    ],
  },
  {
    org: "GINA",
    document_title:
      "GINA Global Strategy for Asthma Management and Prevention — 2024 Main Report",
    document_type: "Clinical Practice Guideline",
    year: 2024,
    canonical_url: "https://ginasthma.org/2024-report/",
    tags: [
      "asthma management",
      "asthma stepwise therapy",
      "inhaled corticosteroid",
      "ics laba",
      "asthma step up",
      "asthma step down",
      "asthma controller",
      "reliever therapy",
      "asthma action plan",
      "asthma severity classification",
      "moderate persistent asthma",
      "mild intermittent asthma",
      "asthma exacerbation",
      "formoterol prn",
      "anti inflammatory reliever",
    ],
  },
  {
    org: "AAAAI/ACAAI",
    document_title:
      "AAAAI/ACAAI Practice Parameter: Evaluation of the Patient with Primary Immunodeficiency",
    document_type: "Practice Parameter",
    year: 2015,
    canonical_url: "https://pubmed.ncbi.nlm.nih.gov/26589585/",
    tags: [
      "primary immunodeficiency",
      "ivig",
      "intravenous immunoglobulin",
      "subcutaneous immunoglobulin",
      "scig",
      "common variable immunodeficiency",
      "cvid",
      "hypogammaglobulinemia",
      "igg replacement",
      "immunoglobulin replacement therapy",
      "antibody deficiency",
      "gammagard",
      "hizentra",
      "specific antibody deficiency",
    ],
  },
  {
    org: "EPOS/EUFOREA",
    document_title:
      "European Position Paper on Rhinosinusitis and Nasal Polyps 2020 (EPOS2020)",
    document_type: "Clinical Practice Guideline",
    year: 2020,
    canonical_url: "https://pubmed.ncbi.nlm.nih.gov/32077450/",
    tags: [
      "chronic rhinosinusitis",
      "nasal polyps",
      "crswNP",
      "crs without nasal polyps",
      "endoscopic sinus surgery",
      "fess",
      "biologics nasal polyps",
      "dupilumab nasal polyps",
      "omalizumab nasal polyps",
      "mepolizumab nasal polyps",
      "type 2 inflammation sinusitis",
      "intranasal corticosteroid",
      "nasal polyp score",
      "snot 22",
      "aspirin exacerbated respiratory disease",
      "aerd",
      "n-erd",
      "allergic fungal rhinosinusitis",
      "integrated care pathway sinusitis",
    ],
  },
  {
    org: "AAAAI/ACAAI",
    document_title:
      "JTFPP GRADE Guidelines for Medical Management of Chronic Rhinosinusitis with Nasal Polyposis",
    document_type: "GRADE Guideline",
    year: 2023,
    canonical_url: "https://pubmed.ncbi.nlm.nih.gov/36370881/",
    tags: [
      "crswNP biologics",
      "nasal polyposis biologics",
      "dupilumab nasal polyps grade",
      "omalizumab nasal polyps grade",
      "mepolizumab nasal polyps grade",
      "intranasal corticosteroid crswNP",
      "aspirin desensitization",
      "atad",
      "aerd treatment",
      "chronic rhinosinusitis medical management",
      "nasal polyp biologic criteria",
      "sinus biologic prior authorization",
      "tezepelumab nasal polyps",
    ],
  },
  {
    org: "AAAAI/ACAAI",
    document_title:
      "AAAAI/ACAAI Stinging Insect Hypersensitivity: A Practice Parameter Update 2016",
    document_type: "Practice Parameter",
    year: 2016,
    canonical_url: "https://pubmed.ncbi.nlm.nih.gov/28007086/",
    tags: [
      "stinging insect allergy",
      "venom immunotherapy",
      "venom allergy",
      "hymenoptera allergy",
      "bee sting allergy",
      "wasp sting allergy",
      "yellow jacket allergy",
      "fire ant allergy",
      "venom skin testing",
      "vit",
      "insect sting anaphylaxis",
      "serum tryptase",
      "mastocytosis venom",
      "venom immunotherapy discontinuation",
      "large local reaction insect sting",
    ],
  },
  {
    org: "AAAAI/ACAAI",
    document_title:
      "AAAAI/ACAAI Contact Dermatitis: A Practice Parameter Update 2015",
    document_type: "Practice Parameter",
    year: 2015,
    canonical_url: "https://pubmed.ncbi.nlm.nih.gov/25965350/",
    tags: [
      "contact dermatitis",
      "allergic contact dermatitis",
      "patch testing",
      "patch test",
      "irritant contact dermatitis",
      "occupational dermatitis",
      "nickel allergy",
      "fragrance allergy",
      "preservative allergy",
      "rubber allergy",
      "latex allergy",
      "t.r.u.e. test",
      "contact allergen",
      "angry back syndrome",
      "dermatitis hands",
    ],
  },
  {
    org: "ICAR",
    document_title:
      "International Consensus Statement on Allergy and Rhinology: Rhinosinusitis 2021 (ICAR-RS)",
    document_type: "Consensus Statement",
    year: 2021,
    canonical_url: "https://pubmed.ncbi.nlm.nih.gov/33236525/",
    tags: [
      "rhinosinusitis consensus",
      "chronic sinusitis management",
      "acute rhinosinusitis",
      "recurrent acute rhinosinusitis",
      "functional endoscopic sinus surgery",
      "fess indications",
      "balloon sinuplasty",
      "ct sinusitis",
      "sinus surgery criteria",
      "maxillary sinusitis",
      "frontal sinusitis",
      "ethmoid sinusitis",
      "saline irrigation sinusitis",
      "oral corticosteroid sinusitis",
      "antibiotic sinusitis",
    ],
  },
  {
    org: "ARIA/EAACI",
    document_title:
      "ARIA-EAACI Guidelines 2024–2025: Allergic Rhinitis Intranasal Treatments",
    document_type: "Clinical Practice Guideline",
    year: 2025,
    canonical_url: "https://pubmed.ncbi.nlm.nih.gov/41324154/",
    tags: [
      "aria allergic rhinitis",
      "allergic rhinitis intranasal treatment",
      "intranasal corticosteroid allergic rhinitis",
      "azelastine fluticasone",
      "mometasone allergic rhinitis",
      "antihistamine nasal spray",
      "allergic rhinitis grade",
      "intermittent allergic rhinitis",
      "persistent allergic rhinitis",
      "seasonal allergic rhinitis treatment",
      "perennial allergic rhinitis",
      "ar pharmacotherapy",
      "allergic rhinitis who guidelines",
    ],
  },

  // TODO: Expand with NCCN, ASGE, additional AAOS, pediatrics
];

// Build canonical URL set for fast lookup
const CANONICAL_URL_SET = new Set(CURATED_INDEX.map((g) => g.canonical_url));

// ============================================
// URL QUALITY GATE
// ============================================

/** Known generic landing page patterns — never link to these */
const GENERIC_LANDING_PATTERNS = [
  /^https?:\/\/www\.acog\.org\/clinical\/clinical-guidance\/?$/i,
  /^https?:\/\/www\.acr\.org\/Clinical-Resources\/ACR-Appropriateness-Criteria\/?$/i,
  /^https?:\/\/www\.aan\.com\/practice\/guidelines\/?$/i,
  /^https?:\/\/www\.aaos\.org\/quality\/quality-programs\/clinical-practice-guidelines\/?$/i,
  /^https?:\/\/www\.cancer\.org\/?\s*$/i,
  /^https?:\/\/www\.nccn\.org\/guidelines\/?$/i,
  /^https?:\/\/www\.cms\.gov\/medicare-coverage-database\/?$/i,
  /^https?:\/\/www\.uspreventiveservicestaskforce\.org\/uspstf\/recommendation-topics\/?$/i,
  /^https?:\/\/www\.spine\.org\/Research-Clinical-Care\/?$/i,
  /^https?:\/\/www\.asge\.org\/home\/resources\/publications\/guidelines\/?$/i,
  /^https?:\/\/www\.auanet\.org\/guidelines-and-quality\/guidelines\/?$/i,
];

/** URL path segments that indicate document-level specificity */
const DOCUMENT_INDICATORS = [
  "practice-bulletin",
  "committee-opinion",
  "appropriateness-criteria",
  "guideline-detail",
  "guidelinedetail",
  "clinical-guidance/practice-bulletin",
  "clinical-guidance/committee-opinion",
  "/pdf/",
  "/doi/",
  "/fulltext/",
  "Narrative/",
  "ncd.aspx",
  "/recommendation/",
  "/article/",
  "guidelines-detail",
  "/docs/",
  "detection-diagnosis-staging",
  "quality-programs/lower-extremity",
  "quality-programs/upper-extremity",
  "Coverage-Policy-Recommendations",
  "Clinical-Guidelines/",
  "Clinical-Guidelines-Archives/",
  "osteoarthritis-of-the-knee",
];

export function classifyUrl(url: string | null): UrlQuality {
  if (!url) return "INVALID";

  // Trim and basic format check
  const trimmed = url.trim();
  if (!trimmed.startsWith("http")) return "INVALID";

  // Check against known generic landing pages
  for (const pattern of GENERIC_LANDING_PATTERNS) {
    if (pattern.test(trimmed)) return "GENERIC_LANDING";
  }

  // Check for document-level indicators in URL path
  const urlLower = trimmed.toLowerCase();
  for (const indicator of DOCUMENT_INDICATORS) {
    if (urlLower.includes(indicator.toLowerCase())) return "DOCUMENT_SPECIFIC";
  }

  // Check if it's in our canonical set
  if (CANONICAL_URL_SET.has(trimmed)) return "DOCUMENT_SPECIFIC";

  // URLs with long paths (4+ segments) are more likely document-specific
  try {
    const parsed = new URL(trimmed);
    const segments = parsed.pathname.split("/").filter(Boolean);
    if (segments.length >= 4) return "DOCUMENT_SPECIFIC";
  } catch {
    return "INVALID";
  }

  return "UNKNOWN";
}

// ============================================
// RESOLVER
// ============================================

/** Normalize a string for fuzzy comparison */
function normalize(s: string): string {
  return s
    .toLowerCase()
    .replace(/['']/g, "")
    .replace(/[^a-z0-9\s]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

/** Score how well a LLM evidence item matches a curated guideline */
function matchScore(item: EvidenceItem, curated: CuratedGuideline): number {
  let score = 0;

  // Org match (required for any score)
  const itemOrg = normalize(item.org);
  const curatedOrg = normalize(curated.org);
  if (!itemOrg.includes(curatedOrg) && !curatedOrg.includes(itemOrg)) {
    return 0; // Wrong org = no match
  }
  score += 10;

  // Document title similarity
  const itemTitle = normalize(item.document_title);
  const curatedTitle = normalize(curated.document_title);

  // Exact substring match
  if (itemTitle.includes(curatedTitle) || curatedTitle.includes(itemTitle)) {
    score += 50;
  } else {
    // Word overlap scoring
    const itemWords = new Set(itemTitle.split(" ").filter((w) => w.length > 2));
    const curatedWords = new Set(
      curatedTitle.split(" ").filter((w) => w.length > 2)
    );
    let overlap = 0;
    for (const w of itemWords) {
      if (curatedWords.has(w)) overlap++;
    }
    const overlapRatio = overlap / Math.max(curatedWords.size, 1);
    score += Math.round(overlapRatio * 40);
  }

  // Year match
  if (item.year && curated.year && item.year === curated.year) {
    score += 10;
  }

  // Tag matching (recommendation text might reference tags)
  const recText = normalize(item.recommendation_text || "");
  for (const tag of curated.tags) {
    if (recText.includes(tag) || itemTitle.includes(tag)) {
      score += 3;
    }
  }

  // Bulletin number matching (e.g., "128", "136", "228")
  const bulletinNumberMatch = item.document_title.match(/\b(\d{2,3})\b/);
  const curatedNumberMatch = curated.document_title.match(/\b(\d{2,3})\b/);
  if (
    bulletinNumberMatch &&
    curatedNumberMatch &&
    bulletinNumberMatch[1] === curatedNumberMatch[1]
  ) {
    score += 25; // Strong signal
  }

  return score;
}

/** Confidence threshold for accepting a resolver match */
const MATCH_THRESHOLD = 35;

/**
 * Resolve a single LLM evidence item against the curated index.
 * Returns the best-matching curated guideline or null.
 */
export function resolveGuideline(item: EvidenceItem): CuratedGuideline | null {
  let best: CuratedGuideline | null = null;
  let bestScore = 0;

  for (const curated of CURATED_INDEX) {
    const score = matchScore(item, curated);
    if (score > bestScore) {
      bestScore = score;
      best = curated;
    }
  }

  return bestScore >= MATCH_THRESHOLD ? best : null;
}

/**
 * Full resolution pipeline for a single evidence item.
 */
export function resolveEvidence(item: EvidenceItem): ResolvedEvidence {
  const match = resolveGuideline(item);

  if (match) {
    // High confidence match — use canonical URL and verified title
    return {
      ...item,
      document_title: match.document_title,
      document_type: match.document_type,
      year: match.year ?? item.year,
      verified: true,
      canonical_url: match.canonical_url,
      url_quality: "DOCUMENT_SPECIFIC",
      display_label: formatDisplayLabel(
        match.org,
        match.document_title,
        match.document_type,
        match.year
      ),
    };
  }

  // No curated match — validate LLM-provided URL
  const urlQuality = classifyUrl(item.url);
  const usableUrl = urlQuality === "DOCUMENT_SPECIFIC" ? item.url : null;

  return {
    ...item,
    verified: false,
    canonical_url: usableUrl,
    url_quality: urlQuality,
    display_label: formatDisplayLabel(
      item.org,
      item.document_title,
      item.document_type,
      item.year
    ),
  };
}

/**
 * Resolve all evidence items, consolidate duplicates, and sort by tier.
 */
export function resolveAllEvidence(items: EvidenceItem[]): ResolvedEvidence[] {
  // Resolve each item
  const resolved = items.map(resolveEvidence);

  // Consolidate duplicates (same canonical_url or same display_label)
  const consolidated = new Map<string, ResolvedEvidence>();
  for (const item of resolved) {
    const key = item.canonical_url || item.display_label;
    if (!consolidated.has(key)) {
      consolidated.set(key, item);
    }
    // If duplicate, keep the one with more detail (longer recommendation)
  }

  // Sort: TIER_1 verified first, then TIER_1 unverified, then TIER_2
  const result = Array.from(consolidated.values());
  result.sort((a, b) => {
    if (a.tier !== b.tier) return a.tier === "TIER_1" ? -1 : 1;
    if (a.verified !== b.verified) return a.verified ? -1 : 1;
    return 0;
  });

  return result;
}

// ============================================
// DISPLAY HELPERS
// ============================================

function formatDisplayLabel(
  org: string,
  title: string,
  type: string,
  year: number | null
): string {
  // If title already contains org, don't duplicate
  const titleLower = title.toLowerCase();
  const orgLower = org.toLowerCase();

  if (titleLower.startsWith(orgLower)) {
    return year ? `${title} (${year})` : title;
  }

  return year ? `${org} ${title} (${year})` : `${org} — ${title}`;
}

// ============================================
// PARSE LLM OUTPUT
// Handles both new JSON format and legacy string format
// ============================================

/**
 * Attempt to parse structured JSON evidence from LLM output.
 * Returns null if content is not JSON format.
 */
export function parseJsonEvidence(content: string): EvidenceItem[] | null {
  // Try to find JSON array in the content
  const jsonMatch =
    content.match(/```json\s*([\s\S]*?)```/) || content.match(/(\[[\s\S]*\])/);

  if (!jsonMatch) return null;

  try {
    const parsed = JSON.parse(jsonMatch[1].trim());
    if (!Array.isArray(parsed)) return null;

    // Validate each item has required fields
    return parsed
      .filter((item: unknown): item is EvidenceItem => {
        if (!item || typeof item !== "object") return false;
        const obj = item as Record<string, unknown>;
        return (
          typeof obj.org === "string" &&
          typeof obj.document_title === "string" &&
          typeof obj.recommendation_text === "string"
        );
      })
      .map((item) => ({
        org: item.org,
        document_title: item.document_title,
        document_type: item.document_type || "Guideline",
        year: typeof item.year === "number" ? item.year : null,
        recommendation_text: item.recommendation_text,
        url: typeof item.url === "string" ? item.url : null,
        tier: item.tier === "TIER_2" ? "TIER_2" : "TIER_1",
      }));
  } catch {
    return null;
  }
}

/**
 * Parse legacy string-based evidence (backward compatibility).
 * Extracts document-level clues from markdown bullet lines.
 */
export function parseLegacyEvidence(content: string): EvidenceItem[] {
  const items: EvidenceItem[] = [];
  const lines = content.split("\n").filter((l) => l.trim());

  for (const line of lines) {
    let cleanLine = line
      .replace(/^[\s\-\*•\d\.]+/, "")
      .replace(/\*\*/g, "")
      .trim();
    if (!cleanLine || cleanLine.length < 15) continue;

    // Extract URL if present (pipe format)
    let url: string | null = null;
    const pipeMatch = cleanLine.match(/\s*\|\s*(https?:\/\/[^\s]+)\s*$/);
    if (pipeMatch) {
      url = pipeMatch[1];
      cleanLine = cleanLine.replace(pipeMatch[0], "").trim();
    }

    // Try to extract org and document info
    const orgMatch = cleanLine.match(
      /^(ACR|ACOG|ACC|AHA|ACS|NASS|AAOS|NCCN|ASGE|ACG|AGA|AAN|ATS|CHEST|AUA|ADA|USPSTF|CMS|ASTRO|Fleischner)/i
    );
    const org = orgMatch ? orgMatch[1].toUpperCase() : "";
    if (!org) continue; // Skip lines without recognized org

    // Extract year
    const yearMatch = cleanLine.match(/\b(19|20)\d{2}\b/);
    const year = yearMatch ? parseInt(yearMatch[0]) : null;

    // Extract document type clues
    let docType = "Guideline";
    if (/practice bulletin/i.test(cleanLine)) docType = "Practice Bulletin";
    else if (/committee opinion/i.test(cleanLine))
      docType = "Committee Opinion";
    else if (/appropriateness criteria/i.test(cleanLine))
      docType = "Appropriateness Criteria";
    else if (/recommendation statement/i.test(cleanLine))
      docType = "Recommendation Statement";
    else if (/standards of care/i.test(cleanLine))
      docType = "Standards of Care";
    else if (/ncd|coverage determination/i.test(cleanLine))
      docType = "National Coverage Determination";
    else if (/consensus/i.test(cleanLine)) docType = "Consensus Statement";

    // Split on " — " or " - " to separate title from recommendation
    const dashSplit = cleanLine.split(/\s*[—–-]\s*/);
    let docTitle = "";
    let recommendation = cleanLine;

    if (dashSplit.length >= 2) {
      // First part likely contains org + doc name, rest is recommendation
      docTitle = dashSplit[0]
        .replace(
          new RegExp(`^${org}\\s*(Guidelines?|Criteria)?\\s*:?\\s*`, "i"),
          ""
        )
        .trim();
      recommendation = dashSplit.slice(1).join(" — ").trim();
    }

    // Determine tier
    const hasDocLevel =
      docTitle.length > 10 ||
      /bulletin|opinion|criteria|guideline|ncd|recommendation/i.test(cleanLine);
    const tier = hasDocLevel ? ("TIER_1" as const) : ("TIER_2" as const);

    items.push({
      org,
      document_title: docTitle || `${org} ${docType}`,
      document_type: docType,
      year,
      recommendation_text: recommendation,
      url,
      tier,
    });
  }

  return items;
}

/**
 * Main entry point — parse evidence content (JSON or legacy) and resolve.
 */
export function parseAndResolveEvidence(content: string): ResolvedEvidence[] {
  // Try JSON first
  const jsonItems = parseJsonEvidence(content);
  const items = jsonItems ?? parseLegacyEvidence(content);

  if (items.length === 0) return [];

  return resolveAllEvidence(items);
}
export function findGuidelinesForContext(
  contextText: string,
  maxResults: number = 3
): CuratedGuideline[] {
  if (!contextText?.trim()) return [];

  const searchTokens = contextText
    .toLowerCase()
    .replace(/[^a-z0-9\s\-]/g, " ")
    .split(/\s+/)
    .filter((t) => t.length > 2);

  if (searchTokens.length === 0) return [];

  const scored = CURATED_INDEX.map((guideline) => {
    let score = 0;

    for (const tag of guideline.tags) {
      const tagLower = tag.toLowerCase();

      if (contextText.toLowerCase().includes(tagLower)) {
        score += tagLower.split(/\s+/).length * 5;
        continue;
      }

      for (const token of searchTokens) {
        if (tagLower.includes(token)) {
          score += 2;
        }
      }
    }

    if (contextText.toLowerCase().includes(guideline.org.toLowerCase())) {
      score += 8;
    }

    const titleWords = guideline.document_title
      .toLowerCase()
      .split(/\s+/)
      .filter((w) => w.length > 3);
    for (const tw of titleWords) {
      if (contextText.toLowerCase().includes(tw)) {
        score += 1;
      }
    }

    return { guideline, score };
  });

  return scored
    .filter((s) => s.score >= 6)
    .sort((a, b) => b.score - a.score)
    .slice(0, maxResults)
    .map((s) => s.guideline);
}
